import React, { useState, useRef, useCallback, useEffect } from "react";
import { Button, Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, tokens } from "@fluentui/react-components";
import {
    Send24Regular,
    Dismiss24Regular,
    Attach24Regular,
    Delete24Regular,
    Person20Regular,
    Mail20Regular,
    TextT20Regular,
    Save20Regular,
} from "@fluentui/react-icons";
import {
    graphMailService,
    SendMailAttachment,
    MailAttachment,
    type EntwurfAnhang,
    type EntwurfDetail,
    type EntwurfPayload,
} from "../../services/graphMailService";
import { useUngespeichertFrage, type UngespeichertTexte } from "../redesign/UngespeichertDialog";
import { bestaetigen, hinweisen } from "../redesign/bestaetigen";
import { useMailContext } from "../../context/MailContext";
import { canSendMailbox } from "../../utils/mailboxAccess";
import { OpenWindowConfig, useWindowManager } from "../windowManager/WindowManagerContext";
import { useResponsive } from "../../hooks/useResponsive";
import "./mobil/mailMobil.css";
import { openDokumentAuswahl } from "../documents/auswahl/DokumentAuswahlFenster";
import { vorgangService } from "../../services/vorgangService";
import { openBetreffPicker } from "./BetreffPickerWindow";
import { KATEGORIE_NOTIZBILD } from "../notizbloecke/bildDarstellung";
import {
    anhangMimeTyp,
    ATTACHMENT_MAX_CHARS,
    attachmentRejectReason,
    base64CharsToBytes,
    fileToBase64,
    formatBytes,
    newContentId,
    SEND_PAYLOAD_MAX_CHARS,
} from "./composeSanitize";
import { buildSendBody, InlineImage } from "./composeSendBody";
import { htmlOhneAusrichtung } from "./richtext/tabAusrichtung";
import { RichTextEditor, RichTextEditorHandle } from "./richtext/RichTextEditor";
import { resolveSignatureForMailbox } from "./signatureResolve";
import { signaturAuswahl, signaturOptionen } from "./signaturDropdown";
import { abonniereSignaturAenderung } from "./mailSignaturBus";
import { openMailSettingsWindow } from "./MailSettingsWindow";
import {
    findSignatureNode,
    insertSignatureNode,
    removeSignatureNode,
    replaceSignatureBody,
} from "./signatureDom";
import { mailTemplateService, MailTemplate } from "../../services/mailTemplateService";
import { mailSignatureService, MailSignature } from "../../services/mailSignatureService";
import {
    isMailSendOutcomeUnknown,
    MAIL_SEND_UNBEKANNT_MESSAGE,
    reportMailError,
    versandFehlgeschlagenMeldung,
} from "../../services/mailError";
import {
    ausgangVon,
    neueSendeId,
    warteAufVersand,
    type VersandAusgang,
} from "../../services/mailVersand";
import {
    EmpfaengerFeld,
    bereinigeEmpfaengerListe,
    enthaeltAdresse,
    istZustellbareAdresse,
    verschiebeEmpfaenger,
    zerlegeEmpfaengerEingabe,
} from "./composeRecipients";
import { meldeMailGesendet, vorgaengeDesVersands } from "./versandBus";
import {
    dropArt,
    leseDokumentZuege,
    leseMailZug,
    mailAnhangName,
    ZIEHTYP_DOKUMENT,
    ZIEHTYP_MAIL,
    type DropArt,
} from "../vorgang/detail/todoDrop";
import { validateEmail } from "../forms/validation/email";
import { AntwortZuordnungLeiste } from "./AntwortZuordnungLeiste";
import {
    hatWahl,
    LEERE_WAHL,
    ordneUrsprungZu,
    type AntwortZuordnungAngebot,
    type AntwortZuordnungWahl,
} from "./antwortZuordnung";
import { bindeVerlaufAnZiel } from "./mailVerlaufBinden";
import { mailLinkService } from "../../services/mailLinkService";
import { publiziereZuweisung } from "./mailAssignmentBus";
import { EmpfaengerVorschlagsListe, useEmpfaengerVorschlaege } from "./empfaengerVorschlaege";
import { alleFenster, aktivesDokument } from "../windowManager/browserFenster/fensterRegister";
import { startAbsender } from "./startAbsender";
import { useStandardAbsender } from "./useMailPostfachEinstellungen";

/**
 * Ist ein Empfänger-Chip eine zustellbare Adresse? Ein unvollständiger Chip
 * („mo@") oder einer mit Trenner („sah@courte.com;", T-435) ließ sonst erst
 * der Server mit einer 400 scheitern — die Regel muss mindestens so streng
 * sein wie dessen `isEmail`, siehe `istZustellbareAdresse`.
 */
const istGueltigeEmpfaengerAdresse = (adresse: string): boolean =>
    validateEmail(adresse).valid && istZustellbareAdresse(adresse);

export type ComposeMode = "reply" | "replyAll" | "forward";

export type OpenComposeParams = {
    mailbox?: string | null;
    to?: string[];
    /** Vorbefüllte Cc-Adressen (Reply-All-Prefill, SP2). Optional → rückwärtskompatibel. */
    cc?: string[];
    /** Vorbefüllte Bcc-Adressen (Symmetrie/Forward, selten genutzt). Optional → rückwärtskompatibel. */
    bcc?: string[];
    subject?: string;
    body?: string;
    mode?: ComposeMode;
    /**
     * Original-Message-Id für echtes Threading. Wird an
     * `graphMailService.sendMail(..., { replyToMessageId })` durchgereicht →
     * Backend antwortet via Graph `/messages/{id}/reply` im selben Thread.
     * Optional → bestehende Aufrufer bleiben unverändert.
     */
    replyToMessageId?: string;
    /**
     * Postfach, in dem die Ursprungsnachricht liegt (2026-07-29).
     *
     * Getrennt vom Absender-Postfach, weil Graph-Message-Ids postfachgebunden
     * sind: Wer aus einem Vorgang heraus antwortet, dessen Mail in einem fremden
     * Postfach liegt, muss als Absender sein EIGENES Postfach wählen können.
     * Vorher brach der Versand dabei ab, weil das Backend das Original im
     * Absender-Postfach suchte.
     */
    replySourceMailbox?: string;
    /**
     * Vorgang, an den die gesendete Mail gehängt wird (PO 26.07.2026, Punkt 13).
     * Ersetzt den früheren `[VG-<id>]`-Marker im Betreff: der stand sichtbar in
     * der Mail und war einen Tastendruck vom Verschwinden entfernt.
     */
    vorgangId?: number;
    /**
     * T-259: To-do, an das die gesendete Mail gehängt wird („Mail schreiben" an
     * einer To-do-Karte). Ohne es hinge die Mail lose am Vorgang, und die
     * Vorgangsansicht zöge sie in ein Übernahme-To-do.
     */
    todoId?: number;
    /**
     * „Erbe den Vorgangsbezug dieser Nachricht" (PO 26.07.2026, Punkt 14) — beim
     * Antworten und Weiterleiten die Id der Ursprungsnachricht. Das Backend löst
     * darüber die Konversation auf und hängt die Antwort an dieselben Vorgänge.
     */
    inheritLinksFromMessageId?: string;
    /**
     * Anhänge der Ursprungsnachricht, die mitgehen sollen — der Weiterleiten-Fall.
     *
     * Bewusst nur die METADATEN (Name, Größe, Typ, Id). Die Dateien selbst
     * bleiben, wo sie sind: Sie in den Browser zu laden, um sie gleich wieder
     * hochzuschicken, würde das Fenster bei großen Anhängen minutenlang
     * blockieren und am Request-Limit scheitern. Beim Senden reist je Anhang
     * nur der Zeiger, und der Server holt die Bytes aus der Originalmail
     * (Postfach dazu: `replySourceMailbox`).
     *
     * Eingebettete Bilder gehören NICHT hierher — die stecken bereits als
     * Bilddaten im zitierten Text und hingen sonst ein zweites Mal an der Mail.
     */
    inheritedAttachments?: MailAttachment[];
    /**
     * Fallback, wenn die Anhangsliste beim Öffnen noch nicht feststand: Das
     * Fenster holt sie dann selbst nach. Greift nur, solange nichts übernommen
     * ist — sonst kämen abgewählte Anhänge zurück, sobald der Nachschlag
     * eintrifft.
     */
    inheritAttachmentsFrom?: { mailbox: string; messageId: string };
    /**
     * Kontakt, dessen KMVS-Dokumente in der Dokument-Auswahl angeboten werden
     * (PO 26.07.2026, Punkt 5). Ohne ihn — und ohne `vorgangId`, aus dem sich
     * der Kunde herleiten lässt — beginnt die Auswahl mit dem Kundenpicker.
     */
    contactId?: number | null;
    /**
     * T-22: Kontakt, zu dem „An"/„Cc"/„Bcc" beim Klick ins Feld Adressen
     * vorschlagen — im Vorgang dessen Kunde (Firma: Zentrale und
     * Ansprechpartner, Person: ihre eigenen Adressen).
     *
     * Bewusst getrennt von `contactId`: der steuert die Dokument-Auswahl und
     * ist beim Antworten der ERKANNTE Absender — bei Vorgangs-Mails aus einem
     * gemeinsamen Postfach oft leer oder eine andere Person. Fehlt der Wert,
     * gilt im Vorgang (`vorgangId`) `contactId` bzw. der nachgeladene Kunde.
     */
    empfaengerKontextId?: number | null;
    /**
     * T-225/T-256: Der Vorgang, in dem diese Mail entsteht — beim Antworten,
     * Allen-Antworten und Weiterleiten der Vorgang der Ursprungsnachricht.
     *
     * NUR Kontext fürs Fenster: Die Dokument-Auswahl zeigt seine Dokumente
     * oben („Dieser Vorgang"), und die Empfänger-Vorschläge kennen alle seine
     * Kontakte. Er VERKNÜPFT die gesendete Mail NICHT — das macht `vorgangId`
     * bzw. beim Antworten `inheritLinksFromMessageId`; beides hier zu setzen,
     * ergäbe Doppel- oder Fehlverknüpfungen, wenn die Konversation an
     * mehreren Vorgängen hängt. Ohne Angabe gilt `vorgangId`.
     */
    bezugsVorgangId?: number | null;
    /**
     * SP8 (A11): Folgeaktion NACH erfolgreichem Versand — z. B. der automatische
     * „Rückruf <Kontakt>"-Vorgang aus dem Anruf-Kontext. Läuft erst, wenn die
     * Mail wirklich raus ist (kein Auto-Vorgang bei Sende-Fehler). Fehlertolerant:
     * Wirft `onSent`, bleibt die Mail dennoch „gesendet"; das Fenster zeigt einen
     * Hinweis und schließt NICHT automatisch. Optional → bestehende Aufrufer
     * bleiben unverändert.
     */
    onSent?: () => void | Promise<void>;
    /**
     * T-212: Ein gespeicherter Entwurf, der hier weitergeschrieben wird
     * (Ordner „Entwürfe", „Entwurf fortsetzen"). Felder, Text und Kontext kommen
     * über die übrigen Parameter herein; hier stehen Id, Stand und die schon im
     * Entwurf liegenden Datei-Anhänge.
     */
    entwurf?: Pick<EntwurfDetail, "id" | "mailbox" | "lastModifiedDateTime" | "attachments">;
    /**
     * T-247: KMVS-Dokumente, die schon beim Öffnen angehängt sind — beim
     * Weiterleiten einer abgelegten Mail-Datei die Datei selbst, damit ihre
     * Anhänge mitreisen. Abwählbar wie jedes gewählte Dokument.
     */
    dokumentAnhaenge?: PickedDocument[];
    /**
     * Mails aus dem Vorgang, die schon beim Öffnen als `.eml` angehängt sind
     * („Als Anhang an neue Mail", User 22.10.2026). Abwählbar wie jeder Anhang.
     */
    mailAnhaenge?: PickedMail[];
    /**
     * Zuweisen beim Antworten (User 02.11.2026): Die Ursprungsmail hängt an
     * keinem Vorgang — das Fenster bietet an, Kontakt und/oder Vorgang/To-do zu
     * wählen. Beim Senden wird erst die Ursprungsmail zugeordnet, die Antwort
     * erbt den Vorgang dann über die Konversation (siehe `antwortZuordnung.ts`).
     */
    antwortZuordnung?: AntwortZuordnungAngebot;
};

/** T-212: Texte der Schließen-Rückfrage im Mailfenster. */
const ENTWURF_FRAGE: UngespeichertTexte = {
    kicker: "E-Mail",
    titel: "Als Entwurf speichern?",
    text: "Diese E-Mail ist noch nicht gesendet. Soll sie als Entwurf im Ordner „Entwürfe“ gespeichert werden? Dort können Sie sie später – auch in Outlook – weiterschreiben und senden.",
    speichern: "Als Entwurf speichern",
};

/** HTTP-Status aus einem fetchJson-Fehler („409 Conflict: …"). */
const statusVon = (err: unknown): number | undefined => {
    const msg = err instanceof Error ? err.message : typeof err === "string" ? err : "";
    const m = /^\s*(\d{3})\b/.exec(msg);
    return m ? Number(m[1]) : undefined;
};

const titleForMode = (mode?: ComposeMode): string =>
    mode === "reply"
        ? "Antworten"
        : mode === "replyAll"
          ? "Allen antworten"
          : mode === "forward"
            ? "Weiterleiten"
            : "Neue E-Mail";

/**
 * Opens the compose/reply editor as a centered window. The window chrome
 * (title bar, minimize/maximize/close) is provided by FloatingWindow — this
 * only mounts the editor content.
 */
export const openComposeWindow = (
    openWindow: (config: OpenWindowConfig) => void,
    params: OpenComposeParams = {},
): string => {
    const id = `mail-compose-${Date.now()}`;
    const W = 680;
    const H = 620;
    // Center within the content area. The window layer sits right of the nav,
    // so nudge left by ~half a sidebar; the window is draggable regardless.
    const x = Math.max(24, Math.round(window.innerWidth / 2 - W / 2 - 150));
    const y = Math.max(16, Math.round(window.innerHeight / 2 - H / 2 - 40));
    openWindow({
        id,
        title: titleForMode(params.mode),
        icon: <Mail20Regular />,
        initialSize: { width: W, height: H },
        initialPosition: { x, y },
        // Handy: Vollbild mit eigenem Kopf (X · Absender · Senden) wie Outlook iOS.
        ohneTitelleisteAufHandy: true,
        render: () => <ComposeMailContent windowId={id} {...params} />,
    });
    return id;
};

/* ── Recipient chip ── */

const FELD_LABEL: Record<EmpfaengerFeld, string> = { to: "An", cc: "Cc", bcc: "Bcc" };

/**
 * Eigener MIME-Typ für das Ziehen eines Empfängers (T-21). Nur Zeilen, die
 * ihn im `dataTransfer` finden, nehmen den Drop an — gezogener Text oder
 * Dateien aus anderen Programmen bleiben unberührt.
 */
export const EMPFAENGER_MIME = "application/x-kmvs-empfaenger";

/**
 * Text in die Zwischenablage. `navigator.clipboard` braucht einen sicheren
 * Kontext (auf PROD per https gegeben) und kann in Edge trotzdem abgelehnt
 * werden — dann greift der alte Weg über ein unsichtbares Textfeld.
 */
async function inZwischenablage(text: string): Promise<boolean> {
    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch {
        /* weiter mit dem Fallback */
    }
    try {
        // Im Dokument, in dem gerade gearbeitet wird (auch im ausgelagerten Browserfenster).
        const dok = aktivesDokument();
        const ta = dok.createElement("textarea");
        ta.value = text;
        ta.setAttribute("readonly", "");
        ta.style.position = "fixed";
        ta.style.opacity = "0";
        dok.body.appendChild(ta);
        ta.select();
        const ok = typeof dok.execCommand === "function" && dok.execCommand("copy");
        dok.body.removeChild(ta);
        return !!ok;
    } catch {
        return false;
    }
}

type ChipProps = {
    value: string;
    onRemove: () => void;
    /**
     * T-21: Feld und Position des Chips. Nur mit beidem (und `onMoveTo`) wird
     * er ziehbar und bekommt das Menü „Verschieben / Kopieren"; ohne bleibt er
     * die reine Anzeige wie vorher.
     */
    feld?: EmpfaengerFeld;
    index?: number;
    onMoveTo?: (nach: EmpfaengerFeld) => void;
    /** Keine gültige Adresse — Chip wird rot und blockiert das Senden. */
    ungueltig?: boolean;
};

const UNGUELTIG_HINWEIS = "Keine gültige E-Mail-Adresse";

const Chip: React.FC<ChipProps> = ({ value, onRemove, feld, index, onMoveTo, ungueltig = false }) => {
    const [kopiert, setKopiert] = useState<null | "ok" | "fehler">(null);
    useEffect(() => {
        if (!kopiert) return;
        const t = setTimeout(() => setKopiert(null), 1500);
        return () => clearTimeout(t);
    }, [kopiert]);

    const interaktiv = feld != null && index != null && onMoveTo != null;

    const inhalt = (
        <>
            <Person20Regular style={{ fontSize: 14 }} />
            {value}
            {kopiert && (
                <span style={{ fontSize: 11, fontWeight: 600, marginLeft: 4 }}>
                    {kopiert === "ok" ? "✓ kopiert" : "Kopieren nicht möglich"}
                </span>
            )}
        </>
    );

    return (
        <span
            draggable={interaktiv || undefined}
            onDragStart={interaktiv ? (e) => {
                e.dataTransfer.setData(EMPFAENGER_MIME, JSON.stringify({ feld, index }));
                // Zusätzlich als Text: dann lässt sich die Adresse auch in
                // andere Programme ziehen.
                e.dataTransfer.setData("text/plain", value);
                e.dataTransfer.effectAllowed = "copyMove";
            } : undefined}
            data-empfaenger-chip={value}
            data-ungueltig={ungueltig || undefined}
            title={ungueltig ? UNGUELTIG_HINWEIS : undefined}
            style={{
                display: "inline-flex", alignItems: "center", gap: 4,
                background: ungueltig ? tokens.colorPaletteRedBackground2 : tokens.colorBrandBackground2,
                color: ungueltig ? tokens.colorPaletteRedForeground1 : tokens.colorBrandForeground2,
                // Rahmen als Schatten: verändert die Chip-Größe nicht.
                boxShadow: ungueltig ? `inset 0 0 0 1px ${tokens.colorPaletteRedBorder2}` : undefined,
                borderRadius: 12, padding: "2px 10px 2px 8px", fontSize: 13, fontWeight: 500,
                whiteSpace: "nowrap", cursor: interaktiv ? "grab" : "default",
            }}
        >
            {interaktiv ? (
                <Menu positioning="below-start">
                    <MenuTrigger>
                        <span
                            title={ungueltig
                                ? `${UNGUELTIG_HINWEIS} — klicken für Optionen`
                                : "Klicken für Optionen, ziehen zum Verschieben (mit Strg: kopieren)"}
                            style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer", outline: "none" }}
                        >
                            {inhalt}
                        </span>
                    </MenuTrigger>
                    <MenuPopover>
                        <MenuList>
                            {(["to", "cc", "bcc"] as const)
                                .filter((f) => f !== feld)
                                .map((f) => (
                                    <MenuItem key={f} onClick={() => onMoveTo!(f)}>
                                        {`Nach ${FELD_LABEL[f]} verschieben`}
                                    </MenuItem>
                                ))}
                            <MenuItem
                                onClick={() => {
                                    void inZwischenablage(value).then((ok) => setKopiert(ok ? "ok" : "fehler"));
                                }}
                            >
                                Adresse kopieren
                            </MenuItem>
                        </MenuList>
                    </MenuPopover>
                </Menu>
            ) : inhalt}
            <button
                onClick={onRemove}
                style={{
                    background: "none", border: "none", cursor: "pointer",
                    padding: "0 0 0 2px", lineHeight: 1, color: "inherit", fontSize: 14,
                    display: "flex", alignItems: "center",
                }}
                title="Entfernen"
            >×</button>
        </span>
    );
};

/**
 * Stabiler Contract für das Empfänger-Feld (To/Cc/Bcc).
 *
 * SP5 (Kontakt-Typeahead) erweitert NUR die Implementierung (Dropdown), NICHT
 * diese Props-Form. Neue Props dürfen ausschließlich **additiv-optional**
 * ergänzt werden (z.B. `enableContactSearch?: boolean` / `kind?: "to"|"cc"|"bcc"`),
 * ohne `label`/`recipients`/`onAdd`/`onRemove` zu brechen.
 */
interface RecipientFieldProps {
    label: string;
    recipients: string[];
    onAdd: (v: string) => void;
    onRemove: (idx: number) => void;
    /**
     * SP5/C2 — schaltet den Kontakt-Typeahead (Dropdown gegen
     * `GET /contact/email-recipients`) frei. Default false → bestehendes
     * Verhalten (reine Freitext-Eingabe) bleibt unverändert. „An"/„Cc"/„Bcc"
     * setzen true.
     */
    enableContactSearch?: boolean;
    /**
     * T-21 — welches Feld das ist. Zusammen mit `onMoveTo`/`onMoveIn` werden
     * die Chips ziehbar, bekommen ein Menü (Verschieben/Kopieren) und die
     * Zeile nimmt Empfänger aus den anderen Feldern per Drop an.
     */
    fieldKey?: EmpfaengerFeld;
    /** T-21: Chip an `index` dieses Felds ins Feld `nach` verschieben (Menü). */
    onMoveTo?: (index: number, nach: EmpfaengerFeld) => void;
    /** T-21: ein Empfänger aus Feld `von` wurde auf dieses Feld gezogen. */
    onMoveIn?: (von: EmpfaengerFeld, index: number, kopie: boolean) => void;
    /**
     * T-22 — Kunde des Vorgangs. Gesetzt, zeigt das Feld schon beim Klick
     * hinein (ohne Tippen) die Adressen zu diesem Kontakt: bei einer Firma
     * die Zentrale und die Ansprechpartner, bei einer Person ihre eigenen.
     * Beim Tippen stehen diese Treffer vorn.
     */
    kontextContactId?: number | null;
    /**
     * T-256 — weitere Kontakte für die Vorschläge beim Klick ins Feld, z. B.
     * alle Kontakte des Vorgangs neben dem Kunden.
     */
    kontextContactIds?: readonly number[];
    /**
     * T-22 — alle schon gewählten Adressen (An, Cc und Bcc). Sie erscheinen
     * nicht noch einmal als Vorschlag. Ohne Angabe: die dieses Felds.
     */
    bereitsGewaehlt?: string[];
}

/** Trägt der laufende Drag einen Empfänger aus dem Verfassen-Fenster? */
const traegtEmpfaenger = (dt: DataTransfer | null | undefined): boolean =>
    !!dt && Array.from(dt.types ?? []).includes(EMPFAENGER_MIME);

const RecipientField: React.FC<RecipientFieldProps> = ({
    label, recipients, onAdd, onRemove, enableContactSearch = false,
    fieldKey, onMoveTo, onMoveIn, kontextContactId = null, kontextContactIds, bereitsGewaehlt,
}) => {
    const [dropAktiv, setDropAktiv] = useState(false);
    const [inputVal, setInputVal] = useState("");
    const [open, setOpen] = useState(false);
    const wrapRef = useRef<HTMLDivElement | null>(null);
    // Vorschlagslogik (Kontextgruppe + Tippsuche) ist mit dem Termin-Fenster
    // geteilt — siehe empfaengerVorschlaege.tsx.
    const vorschlaege = useEmpfaengerVorschlaege({
        aktiv: enableContactSearch,
        offen: open,
        eingabe: inputVal,
        kontextContactId,
        kontextContactIds,
        bereitsGewaehlt: bereitsGewaehlt ?? recipients,
    });

    /**
     * Getippten Text als Empfänger-Chip übernehmen — nur, wenn er ein „@"
     * enthält. Ein Name ohne Adresse („Hahn") wäre als Chip nicht zustellbar;
     * Namen werden über die Trefferliste gewählt. Ohne „@" bleibt der Text im
     * Feld stehen, damit man weitertippen kann („Hahn, Sascha", T-25).
     * Liefert, ob ein Chip übernommen wurde.
     */
    const commit = (text: string = inputVal): boolean => {
        // T-435: „a@x.de; b@y.de;" (Outlook-Gewohnheit, eingefügte Liste)
        // wird zu einzelnen Chips ohne Trenner; Namensteile bleiben stehen.
        const { adressen, rest } = zerlegeEmpfaengerEingabe(text);
        if (adressen.length === 0) return false;
        for (const a of adressen) if (!enthaeltAdresse(recipients, a)) onAdd(a);
        setInputVal(rest); setOpen(false);
        return true;
    };
    const select = (email: string) => {
        onAdd(email);
        setInputVal("");
        // Mit Kundenvorschlägen bleibt die Liste offen: Wer mehrere
        // Ansprechpartner anschreibt, klickt sie nacheinander an.
        if (!vorschlaege.hatKontext) setOpen(false);
    };

    return (
        <div
            ref={wrapRef}
            data-empfaenger-feld={fieldKey}
            onDragOver={onMoveIn ? (e) => {
                if (!traegtEmpfaenger(e.dataTransfer)) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = e.ctrlKey || e.altKey ? "copy" : "move";
                if (!dropAktiv) setDropAktiv(true);
            } : undefined}
            onDragLeave={onMoveIn ? (e) => {
                const nach = e.relatedTarget as Node | null;
                if (nach && wrapRef.current?.contains(nach)) return;
                setDropAktiv(false);
            } : undefined}
            onDrop={onMoveIn ? (e) => {
                setDropAktiv(false);
                if (!traegtEmpfaenger(e.dataTransfer)) return;
                e.preventDefault();
                try {
                    const { feld, index } = JSON.parse(e.dataTransfer.getData(EMPFAENGER_MIME)) as {
                        feld: EmpfaengerFeld; index: number;
                    };
                    if (feld && feld !== fieldKey && Number.isInteger(index)) {
                        onMoveIn(feld, index, e.ctrlKey || e.altKey);
                    }
                } catch {
                    /* fremde oder kaputte Nutzlast — nichts tun */
                }
            } : undefined}
            style={{
                display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4,
                minHeight: 36, padding: "4px 12px",
                borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                background: dropAktiv ? tokens.colorBrandBackground2 : undefined,
                transition: "background 0.1s",
            }}
        >
            <span style={{
                fontSize: 12, fontWeight: 600, color: tokens.colorNeutralForeground3,
                minWidth: 30, userSelect: "none", flexShrink: 0,
            }}>{label}</span>
            {recipients.map((r, i) => (
                <Chip
                    key={`${r}-${i}`}
                    value={r}
                    onRemove={() => onRemove(i)}
                    feld={fieldKey}
                    index={i}
                    onMoveTo={onMoveTo ? (nach) => onMoveTo(i, nach) : undefined}
                    ungueltig={!istGueltigeEmpfaengerAdresse(r)}
                />
            ))}
            <input
                value={inputVal}
                onChange={e => { setInputVal(e.target.value); if (enableContactSearch) setOpen(true); }}
                onFocus={() => { if (enableContactSearch) setOpen(true); }}
                // Wer bei schon fokussiertem Feld erneut hineinklickt, bekommt
                // die Vorschläge wieder (nach Escape oder einer Auswahl).
                onClick={() => { if (enableContactSearch) setOpen(true); }}
                onKeyDown={e => {
                    // Enter nie durchlassen (kein Formular-Submit); das Komma
                    // trennt nur Adressen — nach einem Namen landet es im Feld
                    // und gehört zum Suchtext („Hahn, Sascha", T-25).
                    if (vorschlaege.pfeilTaste(e.key)) { e.preventDefault(); return; }
                    if (e.key === "Enter") {
                        e.preventDefault();
                        const markiert = vorschlaege.markierterTreffer;
                        if (markiert) select(markiert.email); else commit();
                    }
                    if ((e.key === "," || e.key === ";") && commit()) e.preventDefault();
                    if (e.key === "Escape") { setOpen(false); }
                    if (e.key === "Backspace" && !inputVal && recipients.length) onRemove(recipients.length - 1);
                }}
                onBlur={() => { commit(); setOpen(false); }}
                placeholder={recipients.length === 0
                    ? (enableContactSearch ? "Name oder E-Mail suchen…" : "E-Mail-Adresse eingeben...")
                    : ""}
                style={{
                    flex: 1, minWidth: 120, border: "none", outline: "none",
                    background: "transparent", fontSize: 13, color: tokens.colorNeutralForeground1,
                    padding: "2px 0",
                }}
            />
            {enableContactSearch && (
                <EmpfaengerVorschlagsListe
                    vorschlaege={vorschlaege}
                    ankerRef={wrapRef}
                    onSelect={(h) => select(h.email)}
                />
            )}
        </div>
    );
};

/** Trenner in der Werkzeugleiste — hier für die Vorlagen-/Signatur-Dropdowns,
 *  die über `toolbarExtra` in den Editor eingehängt werden. */
const Sep = () => (
    <div style={{ width: 1, height: 20, background: tokens.colorNeutralStroke2, margin: "0 2px" }} />
);

const toggleBtnStyle: React.CSSProperties = {
    background: "none", border: "none", cursor: "pointer",
    fontSize: 12, fontWeight: 600, color: "#361836",
    padding: "4px 8px", borderRadius: 4,
};

// Aktiver Toggle-Zustand (Feld sichtbar): gefüllte Pill statt nur Textlink.
const toggleBtnActiveStyle: React.CSSProperties = {
    background: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground2,
};

const iconFooterBtn: React.CSSProperties = {
    background: "none", border: "none", cursor: "pointer",
    display: "flex", alignItems: "center", justifyContent: "center",
    width: 34, height: 34, borderRadius: 4,
    color: tokens.colorNeutralForeground2,
};

/**
 * Ein KMVS-Dokument als Anhang — nur der Zeiger, nicht der Inhalt. Beim
 * Senden reist `documentId` im `SendMailAttachmentDto`, und das Backend holt
 * die Bytes selbst aus dem Storage. Der naheliegende Weg (Blob laden, base64
 * kodieren, anhängen) schickte die Datei zweimal übers Netz und liefe bei
 * größeren Dokumenten ins Request-Limit.
 */
export type PickedDocument = {
    documentId: number;
    name: string;
    contentType: string;
    size: number;
};

/**
 * Eine Mail aus dem Vorgang als Anhang (User 22.10.2026) — wie Outlooks „Als
 * Anlage weiterleiten". Auch hier reist nur der Zeiger (`linkId` der
 * Verknüpfung); der Server lädt die Mail beim Senden und prüft dabei dieselbe
 * Leseregel wie beim Öffnen im Vorgang. `name` ist „<Betreff>.eml".
 */
export type PickedMail = {
    linkId: number;
    name: string;
};

type ComposeMailContentProps = OpenComposeParams & { windowId: string };

// Export rein additiv (SP1): erlaubt dem Test-Harness, den Inhalt ohne den
// WindowManager zu mounten. Produktivpfad bleibt `openComposeWindow`.
export const ComposeMailContent: React.FC<ComposeMailContentProps> = ({
    windowId,
    mailbox,
    to: initialTo = [],
    cc: initialCc = [],
    bcc: initialBcc = [],
    subject: initialSubject = "",
    body: initialBody = "",
    mode,
    replyToMessageId,
    replySourceMailbox,
    vorgangId,
    todoId,
    inheritLinksFromMessageId,
    inheritedAttachments,
    inheritAttachmentsFrom,
    contactId,
    empfaengerKontextId,
    bezugsVorgangId,
    onSent,
    entwurf,
    dokumentAnhaenge,
    mailAnhaenge: initialMailAnhaenge,
    antwortZuordnung,
}) => {
    const { closeWindow, openWindow, setWindowCloseGuard, requestCloseWindow } = useWindowManager();
    const { currentMailbox, mailboxes, ownMailbox } = useMailContext();
    // Handy: Vollbild wie Outlook iOS — eigener Kopf (X · Absender · Senden),
    // Werkzeugleiste unten, Felder kompakt. Das Fenster dazu hat auf dem Handy
    // keine KMVS-Titelleiste (`ohneTitelleisteAufHandy`).
    const { isPhone: handy } = useResponsive();
    const sendableMailboxes = mailboxes.filter(canSendMailbox);

    /**
     * T-289 (12.10.2026): Startwert des Absenders mit der Einstellung
     * „Standard-Absender" — Regel in `startAbsender`. Was bisher galt
     * (`mailbox ?? currentMailbox`), wird beim Öffnen festgehalten: ein
     * späterer Postfachwechsel in der Mail-App soll das offene Fenster nicht
     * umstellen.
     *
     * Die Einstellung und die Postfachliste kommen womöglich erst NACH dem
     * Öffnen an. Dann wird nachgezogen — aber nur, solange der Nutzer das
     * Absenderfeld nicht selbst geändert hat. Entwürfe bleiben in ihrem
     * Postfach.
     */
    const { wert: standardAbsender } = useStandardAbsender();
    const [bisherigerStart] = useState<string | null>(() => mailbox ?? currentMailbox ?? null);
    // Als Zeichenkette, damit eine neu gebaute, aber gleiche Liste nichts auslöst.
    const sendbareAdressen = sendableMailboxes.map((m) => m.mailboxAddress).join("\n");
    const startWert = React.useMemo(
        () =>
            startAbsender({
                vorgabe: bisherigerStart,
                ursprungsPostfach: replySourceMailbox ?? null,
                eigenesPostfach: ownMailbox,
                standardAbsender,
                sendbar: sendbareAdressen ? sendbareAdressen.split("\n") : [],
            }),
        [bisherigerStart, replySourceMailbox, ownMailbox, standardAbsender, sendbareAdressen],
    );
    const [fromMailbox, setFromMailbox] = useState<string | null>(startWert);
    const absenderVomNutzerRef = React.useRef(false);
    const istEntwurf = !!entwurf;
    React.useEffect(() => {
        if (istEntwurf || absenderVomNutzerRef.current) return;
        setFromMailbox(startWert);
    }, [startWert, istEntwurf]);

    /**
     * Absender-Vorbelegung auf ein Postfach mit echtem Senderecht korrigieren
     * (2026-07-29).
     *
     * Beim Antworten aus einem Vorgang ist `mailbox` das Postfach der
     * ORIGINALmail — darauf hat ein Beteiligter oft nur Lesezugriff über den
     * Vorgang. Diese Adresse stand dann im State, im „Von"-Dropdown gab es dazu
     * aber keinen Eintrag: Das Feld wirkte leer, die Prüfung `if (!fromMailbox)`
     * griff nicht, und der Versand endete im 403 des Backends.
     *
     * Erst wirksam, sobald die Postfachliste geladen ist. Ein vom Nutzer bewusst
     * geleertes Feld bleibt leer.
     */
    const kannSenden = React.useCallback(
        (adresse: string | null | undefined): boolean =>
            !!adresse &&
            sendableMailboxes.some(
                (m) => m.mailboxAddress.toLowerCase() === adresse.toLowerCase(),
            ),
        [sendableMailboxes],
    );
    React.useEffect(() => {
        if (sendableMailboxes.length === 0) return;
        if (!fromMailbox) return;
        if (kannSenden(fromMailbox)) return;
        setFromMailbox(
            kannSenden(currentMailbox) ? currentMailbox! : sendableMailboxes[0].mailboxAddress,
        );
    }, [sendableMailboxes, fromMailbox, currentMailbox, kannSenden]);
    // T-435: Vorbefüllte Listen und geladene Entwürfe können Trenner tragen.
    const [to, setTo] = useState<string[]>(() => bereinigeEmpfaengerListe(initialTo));
    const [cc, setCc] = useState<string[]>(() => bereinigeEmpfaengerListe(initialCc));
    const [bcc, setBcc] = useState<string[]>(() => bereinigeEmpfaengerListe(initialBcc));
    const [subject, setSubject] = useState(initialSubject);
    // KMVS-Dokumente werden als Id angehängt, nicht als Bytes — sie stehen
    // deshalb neben `attachments` (lokale Dateien) in einer eigenen Liste
    // (PO 26.07.2026, Punkt 5).
    const [kmvsDocs, setKmvsDocs] = useState<PickedDocument[]>(() => dokumentAnhaenge ?? []);
    // Mails aus dem Vorgang als `.eml` (User 22.10.2026) — ebenfalls nur Zeiger.
    const [mailAnhaenge, setMailAnhaenge] = useState<PickedMail[]>(() => initialMailAnhaenge ?? []);
    /**
     * Der Vorgang, in dem die Mail entsteht (T-225/T-256), samt Kunde und
     * weiteren Kontakten. Eine Mail aus dem Vorgang heraus kennt oft nur den
     * Vorgang; ohne diesen Nachschlag stünde die Dokument-Auswahl beim
     * Kundenpicker, obwohl der Kunde feststeht. Ein Fehlschlag bleibt
     * folgenlos — dann bleibt eben der Picker.
     */
    const bezugsVorgangIdWirksam = bezugsVorgangId ?? vorgangId ?? null;
    const [bezugsVorgang, setBezugsVorgang] = useState<{
        id: number;
        titel: string | null;
        kunde: { id: number; name: string } | null;
        contactIds: number[];
    } | null>(null);
    useEffect(() => {
        if (bezugsVorgangIdWirksam == null) return;
        let abgebrochen = false;
        void vorgangService
            .getVorgang(bezugsVorgangIdWirksam)
            .then((v) => {
                if (abgebrochen || !v) return;
                const kunde = v.kunde?.id
                    ? {
                          id: v.kunde.id,
                          name: `${v.kunde.lastname ?? ""}${v.kunde.firstName ? ", " + v.kunde.firstName : ""}`.trim(),
                      }
                    : null;
                setBezugsVorgang({
                    id: bezugsVorgangIdWirksam,
                    titel: [v.vorgangNbr, v.titel].filter(Boolean).join(" · ") || null,
                    kunde,
                    contactIds: (v.contactIds ?? []).filter((id) => Number.isInteger(id) && id > 0),
                });
            })
            .catch(() => {
                /* Ohne Vorgangsdaten öffnet die Auswahl mit dem Picker. */
            });
        return () => {
            abgebrochen = true;
        };
    }, [bezugsVorgangIdWirksam]);
    const vorgangsKunde = bezugsVorgang?.kunde ?? null;
    // T-22: Wessen Adressen die Empfängerfelder beim Klick vorschlagen.
    const kontextId: number | null =
        empfaengerKontextId ??
        (vorgangId != null ? (contactId ?? vorgangsKunde?.id ?? null) : null);
    // T-256: Alle Kontakte des Vorgangs schlagen ihre Adressen vor — nicht nur
    // der führende Kunde (Töchter, Firmen, weitere Beteiligte).
    const weitereKontextIds: number[] = bezugsVorgang
        ? [bezugsVorgang.kunde?.id, ...bezugsVorgang.contactIds].filter(
              (id, i, alle): id is number => id != null && id !== kontextId && alle.indexOf(id) === i,
          )
        : [];
    // Schon gewählte Adressen aller drei Felder — die schlägt kein Feld erneut vor.
    const alleGewaehlten = [...to, ...cc, ...bcc];

    // Cc sichtbar, wenn Reply-All ODER bereits vorbefüllt; Bcc nur, wenn vorbefüllt.
    const [showCc, setShowCc] = useState(mode === "replyAll" || initialCc.length > 0);
    const [showBcc, setShowBcc] = useState(initialBcc.length > 0);

    /**
     * T-21: Empfänger zwischen An, Cc und Bcc verschieben (oder kopieren).
     * Ein ausgeblendetes Zielfeld wird eingeblendet — sonst verschwände die
     * Adresse scheinbar.
     */
    const verschiebe = (von: EmpfaengerFeld, index: number, nach: EmpfaengerFeld, kopie = false) => {
        const alt = { to, cc, bcc };
        const neu = verschiebeEmpfaenger(alt, von, index, nach, { kopie });
        if (neu === alt) return;
        setTo(neu.to);
        setCc(neu.cc);
        setBcc(neu.bcc);
        if (nach === "cc") setShowCc(true);
        if (nach === "bcc") setShowBcc(true);
    };
    const verschiebeProps = (feld: EmpfaengerFeld) => ({
        fieldKey: feld,
        onMoveTo: (index: number, nach: EmpfaengerFeld) => verschiebe(feld, index, nach),
        onMoveIn: (von: EmpfaengerFeld, index: number, kopie: boolean) => verschiebe(von, index, feld, kopie),
    });
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState<string | null>(null);
    const [sendSuccess, setSendSuccess] = useState(false);
    // T-45/T-340: Der Ausgang des Versands ließ sich NICHT feststellen — weder
    // der Server kannte ihn noch „Gesendete Elemente“. Erst dann bleibt der
    // Knopf gesperrt, bis der Nutzer nachgesehen und ausdrücklich freigegeben hat.
    const [sendTimedOut, setSendTimedOut] = useState(false);
    // T-340: EINE Versand-Kennung je Verfassen-Fenster. Jeder Versuch aus
    // diesem Fenster trägt sie — der Server sendet zu einer Kennung nie zweimal.
    const sendeIdRef = useRef<string>(neueSendeId());
    // Ab dem zweiten Versuch sieht der Server vorher in „Gesendete Elemente“ nach.
    const schonVersuchtRef = useRef(false);
    const sendetRef = useRef(false);
    /** Zuweisen beim Antworten: die Wahl im Fenster; wirksam erst beim Senden. */
    const [zuordnung, setZuordnung] = useState<AntwortZuordnungWahl>(LEERE_WAHL);
    /** Die Ursprungsmail ist für DIESE Wahl schon zugeordnet — ein zweiter Versuch ordnet nicht erneut zu. */
    const zugeordnetFuerRef = useRef<AntwortZuordnungWahl | null>(null);
    const offenRef = useRef(true);
    useEffect(() => {
        offenRef.current = true;
        return () => {
            offenRef.current = false;
        };
    }, []);
    // SP8: Hinweis, wenn die Mail zwar gesendet wurde, aber die Folgeaktion
    // (`onSent`, z. B. Auto-Rückruf-Vorgang) fehlschlug. Hält das Fenster offen.
    const [postSendWarning, setPostSendWarning] = useState<string | null>(null);
    // SP2: Hinweis, wenn ein eingebettetes Bild nicht in einen Inline-Anhang
    // umgewandelt werden konnte (falsches Format, zu groß, nicht base64). Rein
    // informativ — der Versand läuft weiter, das Bild bleibt eingebettet.
    const [sendWarning, setSendWarning] = useState<string | null>(null);
    const [attachments, setAttachments] = useState<File[]>([]);
    // Anhänge der weitergeleiteten Mail. Nur Metadaten — die Bytes holt der
    // Server beim Senden aus der Ursprungsnachricht (siehe
    // `inheritedAttachments` in OpenComposeParams). Standardmäßig sind alle
    // dabei; wer einen nicht mitschicken will, nimmt ihn hier heraus.
    const [uebernommeneAnhaenge, setUebernommeneAnhaenge] = useState<MailAttachment[]>(
        inheritedAttachments ?? [],
    );
    // Nachschlag für den Fall, dass die Anhangsliste beim Öffnen noch nicht
    // geladen war. Läuft genau einmal und nur, solange nichts übernommen ist —
    // sonst holte er abgewählte Anhänge zurück.
    useEffect(() => {
        // Die Bedingung hängt am ÖFFNUNGS-Zustand, nicht am aktuellen: Wer den
        // einzigen Anhang abwählt, hat auch eine leere Liste — ein Nachschlag
        // würde ihn dann zurückholen.
        if (!inheritAttachmentsFrom || (inheritedAttachments?.length ?? 0) > 0) return;
        let abgebrochen = false;
        void graphMailService
            .fetchAttachments(inheritAttachmentsFrom.mailbox, inheritAttachmentsFrom.messageId)
            .then((liste) => {
                if (abgebrochen) return;
                setUebernommeneAnhaenge(liste.filter((a) => !a.isInline));
            })
            .catch(() => {
                // Stillschweigend: Das Fenster steht bereits, der Absender kann
                // die Datei notfalls von Hand anhängen. Ein Fehlerbanner für
                // einen Nachschlag, den niemand angefordert hat, verwirrt mehr,
                // als es hilft.
            });
        return () => {
            abgebrochen = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [inlineImages, setInlineImages] = useState<InlineImage[]>([]);
    const editorRef = useRef<RichTextEditorHandle>(null);

    // ── T-212: Entwurf ────────────────────────────────────────────────────
    // Id und Stand des Entwurfs im Ordner „Entwürfe" von `entwurfPostfach`.
    // Nach dem ersten Speichern liegt er fest in DIESEM Postfach — deshalb ist
    // der Absender danach nicht mehr umstellbar.
    const [draftId, setDraftId] = useState<string | null>(entwurf?.id ?? null);
    const [entwurfPostfach, setEntwurfPostfach] = useState<string | null>(entwurf?.mailbox ?? null);
    const draftStandRef = useRef<string | null>(entwurf?.lastModifiedDateTime ?? null);
    // Datei-Anhänge, die schon im Entwurf liegen. Sie reisen beim Speichern
    // und Senden nur als Id („behalten") — nicht bei jedem Speichern neu.
    const [entwurfsAnhaenge, setEntwurfsAnhaenge] = useState<EntwurfAnhang[]>(entwurf?.attachments ?? []);
    const [speichertEntwurf, setSpeichertEntwurf] = useState(false);
    const [entwurfHinweis, setEntwurfHinweis] = useState<string | null>(null);
    const { frage: frageEntwurf, dialog: entwurfDialog, setSpeichert: setFrageSpeichert, schliessen: frageSchliessen } =
        useUngespeichertFrage(ENTWURF_FRAGE);
    // „Geändert?" für die Schließen-Rückfrage: Felder per Schnappschuss, der
    // Text per Eingabe-Flag (die automatische Signatur verändert den Text,
    // ohne dass der Nutzer etwas getan hat — ein Vergleich des HTML schlüge an).
    const textGeaendertRef = useRef(false);

    /**
     * Die Dokument-Auswahl öffnen (Büroklammer). Sie liefert beide Quellen in
     * EINEM Ergebnis; die Mail hält sie getrennt, weil KMVS-Dokumente als Id
     * reisen und lokale Dateien als Bytes.
     */
    const oeffneDokumentAuswahl = () => {
        const absender = contactId != null ? { id: contactId, name: null as string | null } : null;
        // T-225: Beim Antworten im Vorgang ist `contactId` der ERKANNTE
        // Absender — oft ein Ansprechpartner oder eine ganz andere Person.
        // Dann zählt der Kunde des Vorgangs; sonst wie bisher der Kontakt.
        const kunde = bezugsVorgangId != null ? (vorgangsKunde ?? absender) : (absender ?? vorgangsKunde);
        openDokumentAuswahl(openWindow, {
            titel: "Dokument anhängen",
            uebernehmenText: "Anhängen",
            contactId: kunde?.id ?? null,
            kundeName: kunde?.name ?? null,
            // T-225: Die Dokumente des Vorgangs stehen in „Aus Vorgängen" oben.
            vorgangId: bezugsVorgangIdWirksam,
            vorgangTitel: bezugsVorgang?.titel ?? null,
            // Was schon an der Mail hängt, ist ausgegraut statt doppelt wählbar.
            ausschluss: new Set(kmvsDocs.map((d) => d.documentId)),
            // T-224 „Mail als Anhang in Mail": Abgelegte Mails (.eml der
            // Kategorie „E-Mail-Archiv") stehen hier — anders als in den
            // übrigen Auswahlen — zur Wahl. Die Liste zeigt nur, was der
            // Nutzer lesen darf, und der Server prüft beim Senden dieselbe
            // Regel wie für die Mail selbst. Notizbilder bleiben draußen.
            ausgeschlosseneKategorien: [KATEGORIE_NOTIZBILD],
            onUebernehmen: ({ dokumente, dateien }) => {
                nimmKmvsDokumente(dokumente);
                nimmDateien(dateien);
            },
        });
    };

    /** KMVS-Dokumente als Zeiger anhängen — entdoppelt über die Id. */
    const nimmKmvsDokumente = (
        dokumente: { id: number; fileName: string; mimeType?: string | null; fileSize?: number | null }[],
    ) => {
        if (dokumente.length === 0) return;
        setKmvsDocs((prev) => {
            const byId = new Map(prev.map((p) => [p.documentId, p]));
            for (const d of dokumente) {
                byId.set(d.id, {
                    documentId: d.id,
                    name: d.fileName,
                    contentType: d.mimeType ?? "application/octet-stream",
                    size: d.fileSize ?? 0,
                });
            }
            return Array.from(byId.values());
        });
    };

    /**
     * Dateien vom Rechner anhängen. Dieselbe Vorab-Prüfung wie beim früheren
     * Dateidialog: zu große oder gesperrte Dateien landeten sonst stumm in der
     * Liste und scheiterten erst beim Senden.
     */
    const nimmDateien = (dateien: File[]) => {
        if (dateien.length === 0) return;
        const abgelehnt = dateien.map(attachmentRejectReason).filter(Boolean) as string[];
        const angenommen = dateien.filter((f) => attachmentRejectReason(f) === null);
        if (angenommen.length) setAttachments((a) => [...a, ...angenommen]);
        setSendError(abgelehnt.length ? abgelehnt[0] : null);
    };

    /**
     * T-225: Ziehen ins Mailfenster — ein Dokument aus dem Vorgang (eigener
     * MIME-Typ) oder Dateien aus dem Windows-Explorer. Bilder, die auf der
     * Schreibfläche landen, fügt der Editor selbst als eingebettetes Bild ein
     * (er setzt dann `defaultPrevented`); alles andere wird Anhang.
     */
    const [ablageAktiv, setAblageAktiv] = useState(false);
    // Was gerade gezogen wird — für die Beschriftung der Ablagezone. Während
    // `dragover` liefert der Browser nur die Typen, nicht die Nutzlast; mehr
    // als die Art braucht der Hinweis aber nicht.
    const [ablageArt, setAblageArt] = useState<DropArt>(null);
    const ziehtAnhang = (dt: DataTransfer | null | undefined): DropArt => {
        const art = dropArt(Array.from(dt?.types ?? []));
        return art === "dokument" || art === "dateien" || art === "mail" ? art : null;
    };
    /**
     * Zieht gerade jemand ein Dokument oder eine Mail aus KMVS (irgendwo auf
     * der Seite)? Dann zeigt das Fenster schon VOR dem Erreichen, dass es den
     * Zug annimmt — sonst sucht der Nutzer das Ziel. `dragstart` feuert nur
     * für Züge aus dieser Seite; Dateien aus Windows melden sich erst über dem
     * Fenster selbst.
     */
    const [zugLaeuft, setZugLaeuft] = useState<DropArt>(null);
    useEffect(() => {
        const start = (e: DragEvent) => {
            const art = dropArt(Array.from(e.dataTransfer?.types ?? []));
            if (art === "mail" || art === "dokument") setZugLaeuft(art);
        };
        const ende = () => setZugLaeuft(null);
        alleFenster.addEventListener("dragstart", start);
        alleFenster.addEventListener("dragend", ende);
        alleFenster.addEventListener("drop", ende);
        // Sicherheitsnetz: Verschwindet die Quelle mitten im Zug (die Liste
        // lädt neu), kommt kein `dragend` mehr. Während eines Zugs gibt es
        // keine Mausbewegung — die erste danach beendet den Hinweis.
        alleFenster.addEventListener("mousemove", ende);
        return () => {
            alleFenster.removeEventListener("dragstart", start);
            alleFenster.removeEventListener("dragend", ende);
            alleFenster.removeEventListener("drop", ende);
            alleFenster.removeEventListener("mousemove", ende);
        };
    }, []);
    /** Mails aus dem Vorgang als `.eml` anhängen — doppelt Gezogenes nur einmal. */
    const nimmMails = (neu: PickedMail[]) => {
        if (neu.length === 0) return;
        setMailAnhaenge((prev) => {
            const ids = new Set(prev.map((m) => m.linkId));
            return [...prev, ...neu.filter((m) => !ids.has(m.linkId))];
        });
    };
    const ablageProps = {
        onDragOver: (e: React.DragEvent) => {
            const art = ziehtAnhang(e.dataTransfer);
            if (!art) return;
            e.preventDefault();
            e.dataTransfer.dropEffect = "copy";
            if (!ablageAktiv) setAblageAktiv(true);
            if (ablageArt !== art) setAblageArt(art);
        },
        onDragLeave: (e: React.DragEvent) => {
            const nach = e.relatedTarget as Node | null;
            if (nach && (e.currentTarget as HTMLElement).contains(nach)) return;
            setAblageAktiv(false);
        },
        onDrop: (e: React.DragEvent) => {
            setAblageAktiv(false);
            if (e.defaultPrevented) return;
            const art = dropArt(Array.from(e.dataTransfer?.types ?? []));
            if (art === "mail") {
                e.preventDefault();
                const zug = leseMailZug(e.dataTransfer.getData(ZIEHTYP_MAIL));
                if (zug?.linkId) {
                    nimmMails([{ linkId: zug.linkId, name: mailAnhangName(zug.subject) }]);
                    setSendError(null);
                } else {
                    setSendError("Diese Mail lässt sich nicht als Anhang übernehmen – bitte aus dem Vorgang heraus ziehen.");
                }
                return;
            }
            if (art === "dokument") {
                e.preventDefault();
                // Einzeln aus dem Vorgang oder mehrere markierte aus dem Reiter
                // „Dokumente" (User 22.10.2026). Ordner lassen sich nicht
                // anhängen — sie werden gemeldet statt still verschluckt.
                const { dokumente, ordner } = leseDokumentZuege(e.dataTransfer.getData(ZIEHTYP_DOKUMENT));
                nimmKmvsDokumente(dokumente);
                if (ordner > 0) {
                    setSendError(
                        dokumente.length > 0
                            ? `${ordner === 1 ? "Der markierte Ordner wurde" : `${ordner} markierte Ordner wurden`} nicht angehängt – Ordner lassen sich nicht anhängen, nur die Dateien darin.`
                            : "Ordner lassen sich nicht anhängen – bitte den Ordner öffnen und die Dateien darin markieren.",
                    );
                } else if (dokumente.length > 0) {
                    setSendError(null);
                }
                return;
            }
            if (art === "dateien") {
                e.preventDefault();
                nimmDateien(Array.from(e.dataTransfer.files ?? []));
            }
        },
    };

    // Vorlagen + Signaturen für die Toolbar-Dropdowns.
    const [templates, setTemplates] = useState<MailTemplate[]>([]);
    const [signatures, setSignatures] = useState<MailSignature[]>([]);
    const [signaturesLoaded, setSignaturesLoaded] = useState(false);
    // Hinweis, wenn die Signatur wegen einer Nutzeränderung NICHT getauscht wurde.
    const [sigNotice, setSigNotice] = useState<string | null>(null);

    /**
     * Zustand der automatisch eingefügten Signatur.
     *
     * `edited` und `removed` sind Endzustände: Hat der Nutzer die Signatur
     * angefasst, gewinnt seine Entscheidung — ein Postfach-Wechsel überschreibt
     * sie dann nicht mehr.
     */
    type SigState =
        | { status: "none" }
        | { status: "inserted"; sigId: number; baseline: string }
        | { status: "edited" }
        | { status: "removed" };
    // T-212: Ein Entwurf bringt seine Signatur schon mit — nicht noch einmal einfügen.
    const sigStateRef = useRef<SigState>(entwurf ? { status: "removed" } : { status: "none" });
    // Während einer laufenden IME-Eingabe (z. B. asiatische Eingabemethoden)
    // darf nicht am DOM operiert werden — das bräche die Komposition ab.
    const composingRef = useRef(false);
    const [compositionTick, setCompositionTick] = useState(0);
    // Aktuelle Inline-Bilder im Ref spiegeln, damit der Unmount-Cleanup (leeres
    // dep-Array) die zuletzt erzeugten Blob-URLs freigeben kann.
    const inlineImagesRef = useRef<InlineImage[]>([]);
    useEffect(() => { inlineImagesRef.current = inlineImages; }, [inlineImages]);

    // Der Anfangsinhalt wird vom Editor selbst gesetzt (`initialHtml`).

    // Vorlagen + Signaturen laden (Toolbar-Dropdowns + automatische Auflösung).
    useEffect(() => {
        let cancelled = false;
        mailTemplateService.list().then((t) => { if (!cancelled) setTemplates(t); }).catch(() => { /* still ignorieren */ });
        mailSignatureService.list()
            .then((s) => { if (!cancelled) setSignatures(s); })
            .catch(() => { /* still ignorieren */ })
            .finally(() => { if (!cancelled) setSignaturesLoaded(true); });
        return () => { cancelled = true; };
    }, []);

    /**
     * Wer über „Signatur erstellen…" gerade eben eine angelegt hat, soll sie in
     * DIESEM Fenster sofort vorfinden — sonst bliebe der Weg folgenlos. Der
     * Service hat seinen Cache beim Speichern bereits geleert, hier wird nur
     * neu geladen. Ist die neue Signatur der Standard des Postfachs, zieht der
     * Effekt darunter sie automatisch in den Text.
     */
    useEffect(() => abonniereSignaturAenderung(() => {
        mailSignatureService.list()
            .then(setSignatures)
            .catch(() => { /* still ignorieren — die alte Liste bleibt gültig */ });
    }), []);

    /**
     * Automatische Signatur: einfügen, austauschen oder entfernen.
     *
     * Aufgelöst wird nach dem GEWÄHLTEN ABSENDER-POSTFACH (Postfach-Standard →
     * allgemeiner Standard → keine), und zwar auch bei Antworten und
     * Weiterleitungen — dort oberhalb des zitierten Verlaufs.
     *
     * Der Zustand liegt in einem Ref, nicht im State: Ein Re-Render darf die
     * Chirurgie nicht erneut auslösen. `edited` und `removed` sind Endzustände —
     * hat der Nutzer die Signatur angefasst, gewinnt seine Entscheidung.
     */
    useEffect(() => {
        const root = editorRef.current?.getRoot();
        if (!root || !signaturesLoaded) return;
        if (composingRef.current) return; // IME-Eingabe läuft → aufschieben

        const target = resolveSignatureForMailbox(signatures, fromMailbox);
        const st = sigStateRef.current;

        if (st.status === "edited" || st.status === "removed") return;

        if (st.status === "none") {
            if (!target?.html) return;
            const { baseline } = insertSignatureNode(root, target.html);
            sigStateRef.current = { status: "inserted", sigId: target.id, baseline };
            return;
        }

        const node = findSignatureNode(root);
        if (!node) {
            // Vom Nutzer gelöscht — nicht wieder einsetzen.
            sigStateRef.current = { status: "removed" };
            return;
        }
        // Verglichen wird ohne das, was das Ausrichten der Tabs ändert
        // (`tab-size` …) — sonst gälte jede Signatur mit Tab als bearbeitet.
        if (htmlOhneAusrichtung(node.innerHTML) !== htmlOhneAusrichtung(st.baseline)) {
            sigStateRef.current = { status: "edited" };
            setSigNotice("Die Signatur wurde bearbeitet und deshalb nicht ausgetauscht.");
            return;
        }
        if (!target?.html) {
            // Für dieses Postfach gibt es keine Signatur.
            removeSignatureNode(node);
            sigStateRef.current = { status: "none" };
            return;
        }
        if (target.id === st.sigId) return; // unverändert
        sigStateRef.current = {
            status: "inserted",
            sigId: target.id,
            baseline: replaceSignatureBody(node, target.html),
        };
        setSigNotice(null);
    }, [signatures, signaturesLoaded, fromMailbox, compositionTick]);

    // Blob-URLs der Inline-Bilder beim Unmount freigeben (Memory-Leak-Schutz).
    useEffect(() => () => {
        inlineImagesRef.current.forEach((i) => {
            try { URL.revokeObjectURL(i.blobUrl); } catch { /* noop */ }
        });
    }, []);

    /**
     * SP7: Vorlage anwenden. Betreff wird gesetzt; der Body wird bei LEEREM
     * Editor ersetzt, sonst am Cursor eingefügt (M4: nicht-destruktiv).
     *
     * Die Leer-Prüfung liegt seit SP3 im Editor (`isEmpty()`) und erkennt jetzt
     * auch `<div><br></div>` als leer — vorher galt der Editor dadurch
     * fälschlich als gefüllt und die Vorlage wurde eingefügt statt ersetzt.
     */
    const applyTemplate = useCallback((tpl: MailTemplate) => {
        setSubject(tpl.subject);
        const editor = editorRef.current;
        if (!editor) return;
        if (editor.isEmpty()) editor.setHtml(tpl.bodyHtml || "");
        else editor.insertHtml(tpl.bodyHtml || "");
    }, []);

    /** SP7: Signatur am Cursor (bzw. am Ende) in den Editor einfügen. */
    const insertSignature = useCallback((sig: MailSignature) => {
        if (!sig.html) return;
        editorRef.current?.insertHtml(sig.html);
    }, []);

    /**
     * Transport-Strategie für ein im Editor eingefügtes Bild: Blob-URL für die
     * Anzeige plus `data-cid` fürs Anhang-Tracking. Der Editor selbst kennt
     * weder cid- noch Mail-Semantik — er baut nur das `<img>` daraus.
     *
     * Bewusst Blob statt `data:`: Ein 680-KB-Bild wären als data:-URI ~928 KB
     * String im `innerHTML` des Editors. Der Base64 wird separat im State
     * gehalten und erst beim Senden angehängt.
     */
    const onInsertImage = useCallback(async (file: File) => {
        const base64 = await fileToBase64(file);
        const contentId = newContentId();
        const blobUrl = URL.createObjectURL(file);
        setInlineImages((imgs) => [...imgs, { contentId, blobUrl, contentType: file.type, base64, name: file.name }]);
        setSendError(null);
        return { src: blobUrl, attrs: { "data-cid": contentId } };
    }, []);

    /**
     * Text und Anhänge so, wie sie gesendet bzw. im Entwurf gespeichert werden.
     * Inline-Bilder (blob: und data:) werden zu `cid:`-Anhängen; KMVS-Dokumente
     * und übernommene Anhänge reisen als Zeiger.
     */
    const sammleNachricht = async () => {
        // (1–3) Inline-Bilder transformieren, sanitisieren, in die App-Sende-
        //        Schrift wrappen und die Inline-Anhänge bauen. Der zitierte
        //        Verlauf bleibt dabei unangetastet — siehe `composeSendBody.ts`.
        const { body: styledHtml, attachments: inlineAtts, warnings } =
            buildSendBody(editorRef.current?.getHtmlFuerVersand() ?? "", inlineImages);
        const fileAtts: SendMailAttachment[] = [];
        for (const file of attachments) {
            fileAtts.push({
                name: file.name,
                // T-224: ohne Browser-Typ (z. B. .msg ohne Outlook) aus der Endung.
                contentType: anhangMimeTyp(file),
                contentBytes: await fileToBase64(file),
                isInline: false,
            });
        }
        // KMVS-Dokumente reisen als Id, nicht als Bytes — der Server holt den
        // Inhalt selbst aus dem Storage.
        const kmvsAtts: SendMailAttachment[] = kmvsDocs.map((d) => ({
            name: d.name,
            contentType: d.contentType,
            documentId: d.documentId,
            isInline: false,
        }));
        // Übernommene Anhänge reisen wie KMVS-Dokumente als Zeiger — der
        // Server holt die Bytes aus der Ursprungsnachricht. `messageId` kommt
        // vom Anhang selbst und nicht aus `inheritLinksFromMessageId`: In
        // einer Konversation kann ein Anhang an einer FRÜHEREN Nachricht des
        // Verlaufs hängen, nicht an der angezeigten.
        const uebernommeneAtts: SendMailAttachment[] = uebernommeneAnhaenge.map((a) => ({
            name: a.name,
            contentType: a.contentType,
            sourceMessageId: a.messageId,
            sourceAttachmentId: a.id,
            isInline: false,
        }));
        // Mails aus dem Vorgang reisen als Verknüpfungs-Id; der Server macht
        // daraus die `.eml` (User 22.10.2026).
        const mailAtts: SendMailAttachment[] = mailAnhaenge.map((m) => ({
            name: m.name,
            contentType: "message/rfc822",
            mailLinkId: m.linkId,
            isInline: false,
        }));
        const allAtts = [...inlineAtts, ...fileAtts, ...kmvsAtts, ...uebernommeneAtts, ...mailAtts];
        return { styledHtml, allAtts, warnings };
    };

    const entwurfKonfliktText =
        "Der Entwurf wurde inzwischen an anderer Stelle geändert (z. B. in Outlook oder von einer Kollegin). " +
        "Bitte das Fenster schließen und den Entwurf neu öffnen.";

    /** T-212: Wire-Inhalt für Speichern/Senden eines Entwurfs. */
    const entwurfPayload = (styledHtml: string, allAtts: SendMailAttachment[]): EntwurfPayload => ({
        mailbox: entwurfPostfach ?? fromMailbox ?? "",
        to,
        cc,
        bcc,
        subject,
        body: styledHtml,
        mode: mode ?? "new",
        replyToMessageId,
        replySourceMailbox: replySourceMailbox ?? undefined,
        vorgangId,
        todoId,
        inheritLinksFromMessageId,
        contactId: contactId ?? undefined,
        attachments: allAtts,
        behalteAnhangIds: entwurfsAnhaenge.map((a) => a.id),
        bekannteAenderung: draftStandRef.current,
    });

    /** Schnappschuss der Felder für „geändert?" (ohne Text, s. `textGeaendertRef`). */
    const feldStand = (werte?: { dateien?: File[]; docs?: PickedDocument[]; mails?: PickedMail[]; entwurfsIds?: string[] }) =>
        JSON.stringify({
            to,
            cc,
            bcc,
            subject,
            d: (werte?.dateien ?? attachments).map((f) => `${f.name}|${f.size}`),
            k: (werte?.docs ?? kmvsDocs).map((d) => d.documentId),
            m: (werte?.mails ?? mailAnhaenge).map((x) => x.linkId),
            e: werte?.entwurfsIds ?? entwurfsAnhaenge.map((a) => a.id),
        });
    const feldBasisRef = useRef<string | null>(null);
    if (feldBasisRef.current === null) feldBasisRef.current = feldStand();
    const istGeaendert = () => textGeaendertRef.current || feldStand() !== feldBasisRef.current;
    const istGeaendertRef = useRef(istGeaendert);
    istGeaendertRef.current = istGeaendert;

    /**
     * T-212: Als Entwurf speichern. Empfänger und Betreff dürfen noch fehlen;
     * nur das Absender-Postfach muss feststehen (dort liegt der Entwurf).
     * Liefert `true` bei Erfolg.
     */
    const handleSaveDraft = async (): Promise<boolean> => {
        const postfach = entwurfPostfach ?? fromMailbox;
        if (!postfach) { setSendError("Bitte ein Absender-Postfach auswählen."); return false; }
        const ungueltig = [...to, ...cc, ...bcc].find((a) => !istGueltigeEmpfaengerAdresse(a));
        if (ungueltig !== undefined) {
            setSendError(`„${ungueltig.trim()}“ ist keine gültige E-Mail-Adresse.`);
            return false;
        }
        setSpeichertEntwurf(true);
        setSendError(null);
        setEntwurfHinweis(null);
        try {
            const { styledHtml, allAtts, warnings } = await sammleNachricht();
            setSendWarning(warnings.length ? warnings[0] : null);
            const tooBig = allAtts.find((a) => (a.contentBytes?.length ?? 0) > ATTACHMENT_MAX_CHARS);
            if (tooBig) {
                setSendError(
                    `Anhang „${tooBig.name}" ist zu groß (max. ${formatBytes(base64CharsToBytes(ATTACHMENT_MAX_CHARS))}).`,
                );
                return false;
            }
            if (allAtts.length > 20) { setSendError("Zu viele Anhänge (max. 20)."); return false; }
            const stand = await graphMailService.saveDraft(
                { ...entwurfPayload(styledHtml, allAtts), mailbox: postfach },
                draftId,
            );
            setDraftId(stand.id);
            setEntwurfPostfach(postfach);
            draftStandRef.current = stand.lastModifiedDateTime;
            // Was jetzt im Entwurf liegt, reist künftig nur noch als Id.
            setEntwurfsAnhaenge(stand.attachments);
            setAttachments([]);
            setKmvsDocs([]);
            setMailAnhaenge([]);
            setUebernommeneAnhaenge([]);
            textGeaendertRef.current = false;
            feldBasisRef.current = feldStand({ dateien: [], docs: [], mails: [], entwurfsIds: stand.attachments.map((a) => a.id) });
            setEntwurfHinweis(`Entwurf gespeichert – liegt im Ordner „Entwürfe“ von ${postfach}.`);
            return true;
        } catch (err: unknown) {
            if (statusVon(err) === 409) {
                void hinweisen({ titel: "Entwurf wurde geändert", text: entwurfKonfliktText });
            }
            setSendError(reportMailError({
                area: "mail",
                action: "save-draft",
                error: err,
                fallbackMessage: "Der Entwurf konnte nicht gespeichert werden.",
            }));
            return false;
        } finally {
            setSpeichertEntwurf(false);
        }
    };
    const handleSaveDraftRef = useRef(handleSaveDraft);
    handleSaveDraftRef.current = handleSaveDraft;
    const fertigRef = useRef(false);
    fertigRef.current = sending || sendSuccess || sendTimedOut;

    /**
     * T-212: Schließen über das Fenster-X fragt bei Ungespeichertem
     * „Als Entwurf speichern / Verwerfen / Abbrechen" — eigenes Popup, kein
     * Browser-Dialog. Kein automatisches Zwischenspeichern.
     */
    useEffect(() => {
        if (!setWindowCloseGuard) return;
        setWindowCloseGuard(windowId, async () => {
            if (fertigRef.current || !istGeaendertRef.current()) return true;
            const wahl = await frageEntwurf();
            if (wahl === "abbrechen") return false;
            if (wahl === "verwerfen") return true;
            setFrageSpeichert(true);
            try {
                return await handleSaveDraftRef.current();
            } finally {
                frageSchliessen();
            }
        });
        return () => setWindowCloseGuard(windowId, null);
    }, [windowId, setWindowCloseGuard, frageEntwurf, setFrageSpeichert, frageSchliessen]);

    /** Papierkorb: ohne Entwurf schließen; mit Entwurf nachfragen und löschen. */
    const handleVerwerfen = async () => {
        if (!draftId || !entwurfPostfach) {
            closeWindow(windowId);
            return;
        }
        const ok = await bestaetigen({
            titel: "Entwurf löschen?",
            text: "Der gespeicherte Entwurf wird gelöscht (er landet in „Gelöschte Elemente“).",
            ja: "Entwurf löschen",
            ton: "danger",
        });
        if (!ok) return;
        try {
            await graphMailService.deleteDraft(entwurfPostfach, draftId);
            closeWindow(windowId);
        } catch (err: unknown) {
            setSendError(reportMailError({
                area: "mail",
                action: "delete",
                error: err,
                fallbackMessage: "Der Entwurf konnte nicht gelöscht werden.",
            }));
        }
    };

    /**
     * T-340: Ausgang beim Server erfragen, bis er feststeht (der Knopf zeigt
     * solange „Wird gesendet …“); „fehlgeschlagen“ gibt den Knopf frei, „unbekannt“
     * sperrt ihn bis zur ausdrücklichen Freigabe.
     */
    const pruefeVersand = async (): Promise<VersandAusgang> => {
        setSendError(null);
        const ausgang = await warteAufVersand(sendeIdRef.current, fromMailbox || undefined, {
            istAbgebrochen: () => !offenRef.current,
        });
        if (!offenRef.current) return ausgang;
        if (ausgang.ausgang === "fehlgeschlagen") {
            setSendTimedOut(false);
            setSendError(versandFehlgeschlagenMeldung(ausgang.fehler));
        } else if (ausgang.ausgang === "unbekannt") {
            setSendTimedOut(true);
            setSendError(MAIL_SEND_UNBEKANNT_MESSAGE);
        }
        return ausgang;
    };

    /** Die Mail ist raus: Hinweise setzen, Folgeaktion, Fenster schließen. */
    const nachErfolg = async (
        result: { linkWarning?: string; linkedVorgangIds?: number[] } | undefined,
    ) => {
        setSendTimedOut(false);
        setSendError(null);
        setSendSuccess(true);
        // Offene Vorgänge zeigen die Mail sofort (User 22.10.2026) — auch wenn
        // die Antwort aus dem Lesefenster kam, das keinen `onSent` kennt, und
        // auch, wenn der Ausgang erst über die Statusabfrage feststand (T-340).
        const vorgangIds = vorgaengeDesVersands(result?.linkedVorgangIds, vorgangId, bezugsVorgangId);
        if (vorgangIds.length > 0) meldeMailGesendet({ vorgangIds });
        // Die Mail ist raus — eine gescheiterte Vorgangs-Verknüpfung ist
        // deshalb KEIN Sende-Fehler, aber sie muss sichtbar sein. Früher
        // landete sie nur im Server-Log und niemand wusste, dass die Mail
        // nicht am Vorgang hängt (PO 26.07.2026).
        setSendWarning(result?.linkWarning ?? null);
        // SP8 (A11): Folgeaktion NACH erfolgreichem Versand. Eigener try/catch,
        // damit ein Fehler hier NICHT als Sende-Fehler gemeldet wird — die Mail
        // IST raus. Bei Fehler bleibt das Fenster offen und zeigt den Hinweis;
        // nur bei Erfolg (oder ohne `onSent`) schließt es automatisch.
        let postSendOk = true;
        if (onSent) {
            try {
                await onSent();
            } catch (postErr: unknown) {
                postSendOk = false;
                setPostSendWarning(
                    reportMailError({
                        area: "mail",
                        action: "post-send",
                        error: postErr,
                        fallbackMessage:
                            "Die E-Mail wurde gesendet, die Folgeaktion ist aber fehlgeschlagen.",
                    }),
                );
            }
        }
        if (postSendOk) setTimeout(() => closeWindow(windowId), 900);
    };

    /** T-340: „Erneut prüfen“ im unklaren Zustand — sendet nie. */
    const handleErneutPruefen = async () => {
        if (sendetRef.current) return;
        sendetRef.current = true;
        setSending(true);
        try {
            const ausgang = await pruefeVersand();
            if (ausgang.ausgang === "gesendet") await nachErfolg(ausgang.ergebnis);
        } finally {
            sendetRef.current = false;
            setSending(false);
        }
    };

    const handleSend = async () => {
        // Doppelklick/Strg+Enter während eines laufenden Versands: ignorieren.
        // Ein Ref statt `sending`, weil der zweite Klick noch den alten
        // Render-Stand sieht.
        if (sendetRef.current || sendSuccess) return;
        if (to.length === 0) { setSendError("Bitte mindestens einen Empfänger angeben."); return; }
        // Ungültige Chips (z. B. „mo@" nach Enter/Verlassen des Feldes) gar
        // nicht erst schicken — der Server lehnt sie ohnehin mit 400 ab.
        const ungueltig = [...to, ...cc, ...bcc].find((a) => !istGueltigeEmpfaengerAdresse(a));
        if (ungueltig !== undefined) {
            setSendError(`„${ungueltig.trim()}“ ist keine gültige E-Mail-Adresse.`);
            return;
        }
        if (!subject.trim()) { setSendError("Bitte einen Betreff eingeben."); return; }
        if (!fromMailbox) { setSendError("Bitte ein Absender-Postfach auswählen."); return; }
        sendetRef.current = true;
        setSending(true);
        setSendError(null);
        try {
            // (1–3) Inline-Bilder (blob: und data:) zu `cid:` transformieren,
            //        sanitisieren, in die App-Sende-Schrift wrappen und die
            //        Inline-Anhänge bauen. Der zitierte Verlauf bleibt dabei
            //        unangetastet — siehe Kopfkommentar in `composeSendBody.ts`.
            const { styledHtml, allAtts, warnings } = await sammleNachricht();
            // Nicht-blockierend: ein nicht konvertierbares Bild bleibt eingebettet,
            // der Versand läuft trotzdem.
            setSendWarning(warnings.length ? warnings[0] : null);

            // (4) Größen-Guards. Sie prüfen die Grenzen, die das Backend real
            //     durchsetzt — vorher scheiterte ein zu großer Anhang erst dort,
            //     mit einer englischen Validierungsmeldung oder einem nackten 413.

            // Guard 1: pro Anhang gegen die DTO-Grenze `@MaxLength` auf
            //          `contentBytes`. Datei-Anhänge werden inzwischen schon beim
            //          Auswählen geprüft; dieser Guard fängt Inline-Bilder und
            //          programmatisch gesetzte Anhänge ab.
            // KMVS-Dokumente tragen hier noch keine Bytes (der Server löst sie
            // auf) — sie können deshalb an dieser Stelle nicht gemessen werden.
            const tooBig = allAtts.find((a) => (a.contentBytes?.length ?? 0) > ATTACHMENT_MAX_CHARS);
            if (tooBig) {
                setSendError(
                    `Anhang „${tooBig.name}" ist zu groß (max. ${formatBytes(base64CharsToBytes(ATTACHMENT_MAX_CHARS))}).`,
                );
                setSending(false);
                return;
            }

            // Guard 2: Anzahl (`@ArrayMaxSize(20)` im Backend).
            if (allAtts.length > 20) {
                setSendError("Zu viele Anhänge (max. 20).");
                setSending(false);
                return;
            }

            // Guard 3: die TATSÄCHLICHE Requestgröße. Die Klammer ist hier nicht
            //          mehr unser `REQUEST_BODY_LIMIT` (12mb), sondern das
            //          Graph-Limit von ~4 MB für die gesamte Nachricht. Bewusst
            //          in Zeichen: der frühere Binär-Guard maß die entpackte
            //          Größe und ließ Requests durch, die ~33 % größer sind.
            // Übernommene Anhänge tragen hier keine Bytes (der Server löst sie
            // auf), ihre Größe ist aus den Metadaten aber bekannt — also
            // mitrechnen, statt den Absender erst nach dem Klick auflaufen zu
            // lassen. `* 4 / 3` ist der base64-Aufschlag, in dem alle anderen
            // Summanden hier gemessen sind.
            const uebernommeneChars = [...uebernommeneAnhaenge, ...entwurfsAnhaenge].reduce(
                (sum, a) => sum + Math.ceil((a.size * 4) / 3),
                0,
            );
            const payloadChars =
                allAtts.reduce((sum, a) => sum + (a.contentBytes?.length ?? 0), 0) +
                uebernommeneChars +
                new TextEncoder().encode(styledHtml).length;
            if (payloadChars > SEND_PAYLOAD_MAX_CHARS) {
                const biggest = [...allAtts].sort(
                    (a, b) => (b.contentBytes?.length ?? 0) - (a.contentBytes?.length ?? 0),
                )[0];
                const biggestKb = Math.round(((biggest?.contentBytes?.length ?? 0) * 3) / 4 / 1024);
                setSendError(
                    `Die Nachricht ist zu groß (max. ${formatBytes(base64CharsToBytes(SEND_PAYLOAD_MAX_CHARS))} inkl. eingebetteter Bilder).` +
                        (biggest ? ` Größter Anhang: „${biggest.name}" (${biggestKb} KB).` : ""),
                );
                setSending(false);
                return;
            }

            // `replyToMessageId` durchreichen → Backend antwortet via Graph
            // `/messages/{id}/reply` im selben Thread (statt neuer Mail).
            // T-212: Gibt es schon einen Entwurf, wird GENAU er gesendet — sonst
            // bliebe er als Leiche in den „Entwürfen" liegen.
            // T-340: mit der Kennung dieses Fensters; ab dem zweiten Versuch als
            // Wiederholung markiert.
            // Zuweisen beim Antworten (User 02.11.2026): ERST die Ursprungsmail
            // zuordnen — die Antwort erbt den Vorgang danach über die
            // Konversation. Scheitert das, geht die Mail nicht raus: sonst
            // stünde die Antwort ohne den gewählten Vorgang da.
            const zuordnungAktiv = !!antwortZuordnung && hatWahl(zuordnung);
            if (antwortZuordnung && zuordnungAktiv && zugeordnetFuerRef.current !== zuordnung) {
                const ursprung = antwortZuordnung.ursprung;
                let meldung: string | null = null;
                try {
                    meldung = await ordneUrsprungZu(zuordnung, {
                        bindeZiel: (ziel) =>
                            bindeVerlaufAnZiel({
                                mailbox: ursprung.mailbox,
                                offen: ursprung,
                                conversationId: ursprung.conversationId ?? null,
                                ziel,
                                // Nichts archivieren: die Mail wird gerade beantwortet.
                                istImPosteingang: () => false,
                            }),
                        verknuepfeKontakt: (contactId) =>
                            mailLinkService.createForContact({ ...ursprung, contactId }),
                    });
                } catch (err: unknown) {
                    setSendError(reportMailError({
                        area: "mail",
                        action: "assign",
                        error: err,
                        fallbackMessage:
                            "Die Zuordnung konnte nicht gespeichert werden. Die Mail wurde nicht gesendet.",
                    }));
                    return;
                }
                zugeordnetFuerRef.current = zuordnung;
                // Liste und offene Fenster derselben Konversation ziehen mit.
                const erstesZiel = zuordnung.ziele[0];
                publiziereZuweisung({
                    mailbox: ursprung.mailbox,
                    messageId: ursprung.messageId,
                    conversationId: ursprung.conversationId ?? null,
                    ...(zuordnung.kontakt ? { kontakt: zuordnung.kontakt } : {}),
                    ...(erstesZiel
                        ? { vorgang: { vorgangId: erstesZiel.vorgangId ?? null, todoId: erstesZiel.todoId ?? null } }
                        : {}),
                });
                if (meldung) void hinweisen({ titel: "Hinweis zum Mailverlauf", text: meldung });
            }
            const kontaktId = zuordnungAktiv ? zuordnung.kontakt?.id : undefined;

            const kennung = { sendeId: sendeIdRef.current, wiederholung: schonVersuchtRef.current };
            schonVersuchtRef.current = true;
            let ausgang: VersandAusgang;
            try {
                const antwort = draftId
                    ? await graphMailService.sendDraft(draftId, entwurfPayload(styledHtml, allAtts), kennung, { kontaktId })
                    : await graphMailService.sendMail(fromMailbox, to, subject, styledHtml, cc, bcc, {
                          replyToMessageId,
                          attachments: allAtts.length > 0 ? allAtts : undefined,
                          vorgangId,
                          todoId,
                          inheritLinksFromMessageId,
                          // Postfach der Ursprungsnachricht — kann vom Absender abweichen,
                          // wenn aus einem Vorgang heraus mit eigenem Absender geantwortet
                          // wird (2026-07-29).
                          replySourceMailbox: replySourceMailbox ?? undefined,
                          kontaktId,
                          ...kennung,
                      });
                ausgang = ausgangVon(antwort);
            } catch (err: unknown) {
                // 502/504 vom Proxy oder eigene Zeitüberschreitung: Der Server
                // arbeitet womöglich weiter — nicht raten, sondern nachfragen.
                if (!isMailSendOutcomeUnknown(err)) throw err;
                ausgang = { ausgang: "laeuft" };
            }
            if (ausgang.ausgang === "laeuft" || ausgang.ausgang === "unbekannt") {
                ausgang = await pruefeVersand();
            }
            if (ausgang.ausgang !== "gesendet") return;
            await nachErfolg(ausgang.ergebnis);
        } catch (err: unknown) {
            if (draftId && statusVon(err) === 409) {
                void hinweisen({ titel: "Entwurf wurde geändert", text: entwurfKonfliktText });
            }
            // Ein echter Fehler (4xx/5xx, Netz): Die Mail ist nicht raus. Ein
            // neuer Versuch trägt dieselbe Kennung — war der erste doch beim
            // Server angekommen, sendet er nicht doppelt (T-340).
            setSendTimedOut(false);
            setSendError(reportMailError({
                area: "mail",
                action: "send",
                error: err,
                fallbackMessage: "Die Mail konnte nicht gesendet werden.",
            }));
        } finally {
            sendetRef.current = false;
            setSending(false);
        }
    };

    return (
        <div
            data-mail-ablage={ablageAktiv || undefined}
            {...ablageProps}
            className={handy ? "mm-compose" : undefined}
            style={{
                height: "100%", display: "flex", flexDirection: "column", position: "relative",
                background: "#ffffff", fontFamily: "'Segoe UI', system-ui, sans-serif",
            }}
        >
            {zugLaeuft && !ablageAktiv && (
                <div
                    aria-hidden
                    data-mail-ablage-bereit
                    style={{
                        position: "absolute", inset: 6, zIndex: 5, pointerEvents: "none",
                        border: "2px dashed #361836", borderRadius: 10,
                        background: "rgba(248, 243, 248, 0.55)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 13, fontWeight: 600, color: "#361836",
                    }}
                >
                    <span style={{ background: "#fff", borderRadius: 999, padding: "4px 12px", boxShadow: "0 1px 4px rgba(0,0,0,0.15)" }}>
                        {zugLaeuft === "mail" ? "Mail hierher ziehen – als Anhang" : "Dokument hierher ziehen – als Anhang"}
                    </span>
                </div>
            )}
            {ablageAktiv && (
                <div
                    aria-hidden
                    style={{
                        position: "absolute", inset: 6, zIndex: 5, pointerEvents: "none",
                        // Gut sichtbar (User 22.10.2026): fast deckend, damit der
                        // Hinweis auch über Text und Bildern lesbar bleibt.
                        border: "3px dashed #361836", borderRadius: 10,
                        background: "rgba(248, 243, 248, 0.94)",
                        boxShadow: "inset 0 0 0 6px rgba(54, 24, 54, 0.06)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 14, fontWeight: 600, color: "#361836",
                    }}
                >
                    <div style={{ textAlign: "center", lineHeight: 1.4 }}>
                        <div style={{ display: "flex", justifyContent: "center", marginBottom: 6 }}>
                            {ablageArt === "mail"
                                ? <Mail20Regular width={36} height={36} />
                                : <Attach24Regular width={36} height={36} />}
                        </div>
                        <div style={{ fontSize: 16, fontWeight: 700 }}>Hier ablegen – als Anhang</div>
                        <div style={{ fontSize: 12, fontWeight: 400, marginTop: 2 }}>
                            {ablageArt === "mail"
                                ? "Die Mail wird als Anlage (.eml) mitgeschickt."
                                : ablageArt === "dokument"
                                  ? "Die Dokumente werden angehängt."
                                  : "Die Dateien werden angehängt."}
                        </div>
                    </div>
                </div>
            )}
            {handy && (
                <div className="mm-compose-kopf">
                    <button
                        type="button"
                        className="mm-compose-rund"
                        aria-label="Schließen"
                        // Fragt bei Inhalt „Als Entwurf speichern / Verwerfen /
                        // Abbrechen" — dieselbe Rückfrage wie am Fenster-X.
                        onClick={() => void (requestCloseWindow ? requestCloseWindow(windowId) : closeWindow(windowId))}
                    >
                        <Dismiss24Regular />
                    </button>
                    <div className="mm-compose-titel">
                        <span className="mm-compose-art">{titleForMode(mode)}</span>
                        {sendableMailboxes.length > 0 && (
                            <label className="mm-compose-von">
                                <span className="mm-label">{entwurfPostfach ?? fromMailbox ?? "Postfach wählen"}</span>
                                {!entwurfPostfach && <span aria-hidden>▾</span>}
                                <select
                                    aria-label="Absender-Postfach"
                                    value={entwurfPostfach ?? fromMailbox ?? ""}
                                    disabled={!!entwurfPostfach}
                                    onChange={(e) => {
                                        absenderVomNutzerRef.current = true;
                                        setFromMailbox(e.target.value || null);
                                    }}
                                >
                                    <option value="">— Postfach wählen —</option>
                                    {sendableMailboxes.map((m) => (
                                        <option key={m.id} value={m.mailboxAddress}>
                                            {m.displayName ? `${m.displayName} <${m.mailboxAddress}>` : m.mailboxAddress}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        )}
                    </div>
                    <button
                        type="button"
                        className="mm-compose-rund mm-compose-senden"
                        aria-label={sendSuccess ? "Gesendet" : sending ? "Wird gesendet" : "Senden"}
                        onClick={handleSend}
                        disabled={sending || sendSuccess || sendTimedOut}
                    >
                        <Send24Regular />
                    </button>
                </div>
            )}
            {/* Recipients */}
            <div style={{ flexShrink: 0, background: "#fff" }}>
                <RecipientField label="An" recipients={to} enableContactSearch {...verschiebeProps("to")} kontextContactId={kontextId} kontextContactIds={weitereKontextIds} bereitsGewaehlt={alleGewaehlten} onAdd={v => setTo(t => [...t, v])} onRemove={i => setTo(t => t.filter((_, x) => x !== i))} />
                {/*
                 * Toggle-Pills für Cc/Bcc IMMER sichtbar (unabhängig vom mode-Startzustand
                 * und davon, ob das jeweils andere Feld offen ist). Jede Pill toggelt ihr
                 * EIGENES Feld auf/zu; aktiv = Feld sichtbar. Beim Schließen bleiben bereits
                 * eingegebene Adressen im State erhalten (kein stiller Datenverlust).
                 */}
                <div style={{ display: "flex", gap: 12, padding: "4px 12px", borderBottom: `1px solid ${tokens.colorNeutralStroke2}` }}>
                    <button
                        onClick={() => setShowCc(v => !v)}
                        style={{ ...toggleBtnStyle, ...(showCc ? toggleBtnActiveStyle : null) }}
                        aria-pressed={showCc}
                        title={showCc ? "Cc ausblenden" : "Cc einblenden"}
                    >Cc</button>
                    <button
                        onClick={() => setShowBcc(v => !v)}
                        style={{ ...toggleBtnStyle, ...(showBcc ? toggleBtnActiveStyle : null) }}
                        aria-pressed={showBcc}
                        title={showBcc ? "Bcc ausblenden" : "Bcc einblenden"}
                    >Bcc</button>
                </div>
                {showCc && (
                    <RecipientField label="Cc" recipients={cc} enableContactSearch {...verschiebeProps("cc")} kontextContactId={kontextId} kontextContactIds={weitereKontextIds} bereitsGewaehlt={alleGewaehlten} onAdd={v => setCc(c => [...c, v])} onRemove={i => setCc(c => c.filter((_, x) => x !== i))} />
                )}
                {showBcc && (
                    <RecipientField label="Bcc" recipients={bcc} enableContactSearch {...verschiebeProps("bcc")} kontextContactId={kontextId} kontextContactIds={weitereKontextIds} bereitsGewaehlt={alleGewaehlten} onAdd={v => setBcc(b => [...b, v])} onRemove={i => setBcc(b => b.filter((_, x) => x !== i))} />
                )}

                {!handy && sendableMailboxes.length > 0 && (
                    <div style={{ borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, padding: "8px 12px", display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ color: tokens.colorNeutralForeground3, fontSize: 13, minWidth: 40 }}>Von</span>
                        <select
                            value={entwurfPostfach ?? fromMailbox ?? ""}
                            onChange={(e) => {
                                absenderVomNutzerRef.current = true;
                                setFromMailbox(e.target.value || null);
                            }}
                            disabled={!!entwurfPostfach}
                            title={entwurfPostfach ? `Der Entwurf liegt im Postfach ${entwurfPostfach} – der Absender lässt sich nicht mehr ändern.` : undefined}
                            style={{
                                flexGrow: 1, border: "none", outline: "none",
                                background: "transparent", fontSize: 14, padding: "4px 0",
                                color: tokens.colorNeutralForeground1,
                            }}
                        >
                            <option value="">— Postfach wählen —</option>
                            {sendableMailboxes.map((m) => (
                                <option key={m.id} value={m.mailboxAddress}>
                                    {m.displayName ? `${m.displayName} <${m.mailboxAddress}>` : m.mailboxAddress}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                <div
                    style={{
                        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
                        padding: "0 12px",
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                    }}
                >
                    <input
                        value={subject}
                        onChange={e => setSubject(e.target.value)}
                        placeholder="Betreff"
                        // T-243: Rechtschreibprüfung des Browsers, auf Deutsch.
                        spellCheck
                        lang="de"
                        style={{
                            flex: 1, minWidth: 0, border: "none", outline: "none",
                            fontSize: 15, fontWeight: 600, padding: "10px 0",
                            color: tokens.colorNeutralForeground1, background: "transparent",
                            fontFamily: "inherit",
                        }}
                    />
                    {/* Betreff nach Schema bauen (PO 27.07.2026): Vertrag und/oder
                        Risikoobjekt wählen statt „Acme - VSNR …" abzutippen. */}
                    <Button
                        appearance="subtle"
                        size="small"
                        icon={<TextT20Regular />}
                        onClick={() =>
                            openBetreffPicker(openWindow, {
                                contactId,
                                subject,
                                onApply: setSubject,
                            })
                        }
                        title="Betreff aus Vertrag und Risikoobjekt zusammensetzen"
                    >
                        Ausfüllen
                    </Button>
                </div>
                {antwortZuordnung && mode && (
                    <AntwortZuordnungLeiste
                        angebot={antwortZuordnung}
                        wahl={zuordnung}
                        onWahl={setZuordnung}
                        openWindow={openWindow}
                        gesperrt={sending || sendSuccess}
                    />
                )}
            </div>

            {/* Werkzeugleiste + Schreibfläche — seit SP3 der gemeinsame Editor.
                Optisch und funktional identisch zur früheren Inline-Fassung; neu
                sind der Selektionsschutz und das Link-Popover statt window.prompt. */}
            <RichTextEditor
                ref={editorRef}
                initialHtml={initialBody}
                // T-243: eigene Rechtschreib- und Grammatikprüfung (LanguageTool).
                rechtschreibung
                // Handy: kein Tab-Lineal — dafür ist kein Platz und kein Bedarf.
                lineal={!handy}
                onInsertImage={onInsertImage}
                onError={setSendError}
                onInput={() => { textGeaendertRef.current = true; }}
                onKeyDown={e => {
                    if ((e.ctrlKey || e.metaKey) && e.key === "Enter") { e.preventDefault(); handleSend(); }
                }}
                // Während einer IME-Komposition (z. B. asiatische Eingabe-
                // methoden) wird die Signatur-Chirurgie ausgesetzt; danach holt
                // der Tick sie nach.
                onCompositionStart={() => { composingRef.current = true; }}
                onCompositionEnd={() => {
                    composingRef.current = false;
                    setCompositionTick(t => t + 1);
                }}
                toolbarExtra={
                    <>
                        {/* Vorlagen- + Signatur-Dropdowns (statischer Body, keine Platzhalter).
                            Bewusst hier statt im Editor: der Editor kennt die Mail-Domäne nicht. */}
                        {templates.length > 0 && (
                            <>
                                <Sep />
                                <select
                                    value=""
                                    title="Vorlage einfügen"
                                    onChange={(e) => {
                                        const id = Number(e.target.value);
                                        e.target.value = "";
                                        const tpl = templates.find((t) => t.id === id);
                                        if (tpl) applyTemplate(tpl);
                                    }}
                                    style={{ fontSize: 12, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: 4, padding: "2px 4px", background: "#fff", cursor: "pointer", maxWidth: 150 }}
                                >
                                    <option value="">Vorlage…</option>
                                    {templates.map((t) => (
                                        <option key={t.id} value={t.id}>{t.name}</option>
                                    ))}
                                </select>
                            </>
                        )}
                        {/* Das Signatur-Dropdown steht IMMER — auch ohne angelegte
                            Signatur, denn dort liegt der letzte Eintrag „Signatur
                            erstellen…" (signaturDropdown.ts). Der Trenner kommt sonst
                            vom Vorlagen-Block; fehlt der, wird er hier nachgeholt. */}
                        {templates.length === 0 && <Sep />}
                        <select
                            value=""
                            title="Signatur einfügen"
                            onChange={(e) => {
                                const wahl = signaturAuswahl(e.target.value, signatures);
                                e.target.value = "";
                                if (!wahl) return;
                                if (wahl.art === "verwalten") openMailSettingsWindow(openWindow, "signatures");
                                else insertSignature(wahl.signatur);
                            }}
                            style={{ fontSize: 12, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: 4, padding: "2px 4px", background: "#fff", cursor: "pointer", maxWidth: 150 }}
                        >
                            <option value="">Signatur…</option>
                            {signaturOptionen(signatures).map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </>
                }
            />

            {/* Attachments */}
            {(attachments.length > 0 || kmvsDocs.length > 0 || mailAnhaenge.length > 0 || uebernommeneAnhaenge.length > 0 || entwurfsAnhaenge.length > 0) && (
                <div style={{
                    display: "flex", gap: 8, padding: "8px 16px", flexWrap: "wrap",
                    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
                    background: tokens.colorNeutralBackground2, flexShrink: 0,
                }}>
                    {/* T-212: Anhänge, die schon im gespeicherten Entwurf liegen. */}
                    {entwurfsAnhaenge.map((a) => (
                        <div key={`entwurf-${a.id}`} style={{
                            display: "flex", alignItems: "center", gap: 6,
                            background: "#fff", border: `1px solid ${tokens.colorNeutralStroke2}`,
                            borderRadius: 4, padding: "4px 10px", fontSize: 12,
                        }}>
                            <Attach24Regular style={{ fontSize: 14, color: tokens.colorNeutralForeground2 }} />
                            <span>{a.name}</span>
                            {a.size > 0 && (
                                <span style={{ color: tokens.colorNeutralForeground3 }}>{formatBytes(a.size)}</span>
                            )}
                            <button
                                onClick={() => setEntwurfsAnhaenge((list) => list.filter((x) => x.id !== a.id))}
                                title="Anhang entfernen"
                                aria-label={`${a.name} entfernen`}
                                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", color: tokens.colorNeutralForeground3 }}>
                                <Dismiss24Regular style={{ fontSize: 14 }} />
                            </button>
                        </div>
                    ))}
                    {/* Anhänge der weitergeleiteten Mail. Zuerst, weil sie schon
                        da sind, bevor der Absender selbst etwas anhängt. */}
                    {uebernommeneAnhaenge.map((a) => (
                        <div key={`uebernommen-${a.messageId}-${a.id}`} style={{
                            display: "flex", alignItems: "center", gap: 6,
                            background: "#fff", border: `1px solid ${tokens.colorNeutralStroke2}`,
                            borderRadius: 4, padding: "4px 10px", fontSize: 12,
                        }}>
                            <Attach24Regular style={{ fontSize: 14, color: tokens.colorNeutralForeground2 }} />
                            <span>{a.name}</span>
                            {a.size > 0 && (
                                <span style={{ color: tokens.colorNeutralForeground3 }}>{formatBytes(a.size)}</span>
                            )}
                            <span
                                style={{ color: tokens.colorNeutralForeground3 }}
                                title="Aus der weitergeleiteten Mail übernommen"
                            >
                                Original
                            </span>
                            <button
                                onClick={() => {
                                    textGeaendertRef.current = true;
                                    setUebernommeneAnhaenge((list) =>
                                        list.filter((x) => !(x.id === a.id && x.messageId === a.messageId)),
                                    );
                                }}
                                title="Nicht mitschicken"
                                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", color: tokens.colorNeutralForeground3 }}>
                                <Dismiss24Regular style={{ fontSize: 14 }} />
                            </button>
                        </div>
                    ))}
                    {kmvsDocs.map((d) => (
                        <div key={`kmvs-${d.documentId}`} style={{
                            display: "flex", alignItems: "center", gap: 6,
                            background: "#fff", border: `1px solid ${tokens.colorNeutralStroke2}`,
                            borderRadius: 4, padding: "4px 10px", fontSize: 12,
                        }}>
                            <Attach24Regular style={{ fontSize: 14, color: tokens.colorNeutralForeground2 }} />
                            <span>{d.name}</span>
                            {d.size > 0 && (
                                <span style={{ color: tokens.colorNeutralForeground3 }}>{formatBytes(d.size)}</span>
                            )}
                            <span style={{ color: tokens.colorNeutralForeground3 }} title="Aus der KMVS-Dokumentenablage">KMVS</span>
                            <button onClick={() => setKmvsDocs(a => a.filter(x => x.documentId !== d.documentId))}
                                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", color: tokens.colorNeutralForeground3 }}>
                                <Dismiss24Regular style={{ fontSize: 14 }} />
                            </button>
                        </div>
                    ))}
                    {mailAnhaenge.map((m) => (
                        <div key={`mail-${m.linkId}`} data-mail-anhang={m.linkId} style={{
                            display: "flex", alignItems: "center", gap: 6,
                            background: "#fff", border: `1px solid ${tokens.colorNeutralStroke2}`,
                            borderRadius: 4, padding: "4px 10px", fontSize: 12,
                        }}>
                            <Mail20Regular style={{ fontSize: 14, color: tokens.colorNeutralForeground2 }} />
                            <span>{m.name}</span>
                            <span
                                style={{ color: tokens.colorNeutralForeground3 }}
                                title="Die Mail geht als Anlage (.eml) mit – der Empfänger öffnet sie wie eine Mail."
                            >
                                Mail
                            </span>
                            <button
                                onClick={() => setMailAnhaenge((list) => list.filter((x) => x.linkId !== m.linkId))}
                                title="Anhang entfernen"
                                aria-label={`${m.name} entfernen`}
                                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", color: tokens.colorNeutralForeground3 }}>
                                <Dismiss24Regular style={{ fontSize: 14 }} />
                            </button>
                        </div>
                    ))}
                    {attachments.map((f, i) => (
                        <div key={i} style={{
                            display: "flex", alignItems: "center", gap: 6,
                            background: "#fff", border: `1px solid ${tokens.colorNeutralStroke2}`,
                            borderRadius: 4, padding: "4px 10px", fontSize: 12,
                        }}>
                            <Attach24Regular style={{ fontSize: 14, color: tokens.colorNeutralForeground2 }} />
                            <span>{f.name}</span>
                            <span style={{ color: tokens.colorNeutralForeground3 }}>{formatBytes(f.size)}</span>
                            <button onClick={() => setAttachments(a => a.filter((_, x) => x !== i))}
                                style={{ background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex", color: tokens.colorNeutralForeground3 }}>
                                <Dismiss24Regular style={{ fontSize: 14 }} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Footer */}
            <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "10px 14px",
                borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
                background: "#fff", flexShrink: 0,
            }}>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    {/* Handy: Senden steht oben rechts im Kopf. */}
                    {!handy && (
                    <button
                        onClick={handleSend}
                        disabled={sending || sendSuccess || sendTimedOut}
                        style={{
                            display: "flex", alignItems: "center", gap: 8,
                            background: sendSuccess ? "#107c10" : sendTimedOut ? "#8a6d1b" : "#361836",
                            color: "#fff", border: "none", borderRadius: 4,
                            padding: "8px 20px", fontSize: 14, fontWeight: 600,
                            cursor: sending ? "wait" : sendTimedOut ? "default" : "pointer",
                            opacity: sending ? 0.85 : 1, transition: "background 0.2s",
                        }}
                    >
                        <Send24Regular style={{ fontSize: 18 }} />
                        {sendSuccess
                            ? "Gesendet ✓"
                            : sending
                              ? "Wird gesendet …"
                              : sendTimedOut
                                ? "Versand unklar"
                                : "Senden"}
                    </button>
                    )}
                    {/* T-45/T-340: Nur wenn KMVS den Ausgang nicht feststellen
                        konnte. „Erneut prüfen“ fragt nur nach (sendet nie);
                        „Erneut freigeben“ gibt den Knopf wieder frei — der
                        nächste Versand trägt dieselbe Kennung, und der Server
                        sieht vorher in „Gesendete Elemente“ nach. Bewusst als
                        unauffällige Textknöpfe, nicht als Hauptaktion. */}
                    {sendTimedOut && !sending && (
                        <>
                            <button
                                onClick={() => void handleErneutPruefen()}
                                style={{
                                    background: "none", border: "none", padding: "4px 6px",
                                    fontSize: 12, color: "#361836", textDecoration: "underline",
                                    cursor: "pointer",
                                }}
                                title="Beim Server nachfragen, ob die Mail gesendet wurde – sendet nicht"
                            >
                                Erneut prüfen
                            </button>
                            <button
                                onClick={() => { setSendTimedOut(false); setSendError(null); }}
                                style={{
                                    background: "none", border: "none", padding: "4px 6px",
                                    fontSize: 12, color: "#6b5a66", textDecoration: "underline",
                                    cursor: "pointer",
                                }}
                                title="Nur nutzen, wenn die Mail unter „Gesendete Elemente“ nicht auftaucht"
                            >
                                Nicht angekommen? Erneut freigeben
                            </button>
                        </>
                    )}
                    {/* EIN Knopf für alle drei Quellen (PO 22.09.2026): die
                        Büroklammer öffnet die Dokument-Auswahl, in der die
                        Datei vom Rechner neben den Dokumenten aus KMVS steht.
                        Vorher waren es zwei Knöpfe nebeneinander, und wer ein
                        Kundendokument suchte, fand zuerst den Dateidialog. */}
                    <button
                        onClick={() => oeffneDokumentAuswahl()}
                        style={iconFooterBtn}
                        title="Dokument anhängen — aus KMVS oder vom Rechner"
                    >
                        <Attach24Regular style={{ fontSize: 20 }} />
                    </button>
                    {/* T-212: wie in Outlook — die Mail liegt danach im Ordner
                        „Entwürfe" und lässt sich später weiterschreiben. */}
                    <button
                        onClick={() => void handleSaveDraft()}
                        disabled={speichertEntwurf || sending || sendSuccess}
                        style={{
                            display: "flex", alignItems: "center", gap: 6,
                            background: "#fff", color: "#361836",
                            border: `1px solid ${tokens.colorNeutralStroke1}`, borderRadius: 4,
                            padding: "6px 12px", fontSize: 13, fontWeight: 600,
                            cursor: speichertEntwurf ? "wait" : "pointer",
                        }}
                        title="Als Entwurf im Ordner „Entwürfe“ speichern – später weiterschreiben und senden"
                    >
                        <Save20Regular style={{ fontSize: 16 }} />
                        {handy ? (speichertEntwurf ? "…" : "Entwurf") : speichertEntwurf ? "Speichert …" : "Als Entwurf speichern"}
                    </button>
                </div>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    {sendError && (
                        <span
                            role={sendTimedOut ? "status" : "alert"}
                            style={{ fontSize: 12, color: sendTimedOut ? "#8a6d1b" : "#a4262c", maxWidth: sendTimedOut ? 320 : 240 }}
                        >⚠ {sendError}</span>
                    )}
                    {entwurfHinweis && !sendError && (
                        <span role="status" style={{ fontSize: 12, color: "#107c10", maxWidth: 300 }}>✓ {entwurfHinweis}</span>
                    )}
                    {sigNotice && !entwurfHinweis && !sendError && !sendWarning && !postSendWarning && (
                        <span style={{ fontSize: 12, color: "#6b5a66", maxWidth: 300 }}>{sigNotice}</span>
                    )}
                    {sendWarning && !sendError && !postSendWarning && (
                        <span style={{ fontSize: 12, color: "#8a6d1b", maxWidth: 300 }}>⚠ {sendWarning}</span>
                    )}
                    {postSendWarning && !sendError && (
                        <span style={{ fontSize: 12, color: "#8a6d1b", maxWidth: 300 }}>
                            ✓ Gesendet — ⚠ {postSendWarning}
                        </span>
                    )}
                    <button
                        onClick={() => void handleVerwerfen()}
                        style={iconFooterBtn}
                        title={draftId ? "Entwurf löschen" : "Verwerfen"}
                        aria-label={draftId ? "Entwurf löschen" : "Verwerfen"}
                    >
                        <Delete24Regular style={{ fontSize: 20 }} />
                    </button>
                </div>
            </div>
            {entwurfDialog}
        </div>
    );
};
